import React, { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  ReceiptLong as ReceiptLongIcon,
  RequestQuote as RequestQuoteIcon,
  TaskAlt as TaskAltIcon,
  ViewSidebar as ViewSidebarIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import { api } from '../contexts/ApiContext';
import { AnnualSectionSignedChartPanel, AnnualSignedInOutChart } from '../components/AnnualSectionSignedChartPanel';
import {
  ANNUAL_DISPLAY_SECTIONS,
  annualPlBucket,
  buildPlClassByName,
  plClassFromReviewRow,
  rowsForAnnualDisplaySection,
  type AnnualPlBucket,
} from '../utils/annualPlClass';
import {
  annualSummaryChartCategory,
  buildSignedInOutCategoryRows,
  buildSignedInOutSupplierRows,
  combineAnnualPlAndCashCharts,
  annualCombinedChartDisplayRows,
  DEFAULT_GBP_TO_EUR_RATE,
  scaleToCurrency,
  foldCategorySummariesIntoCurrency,
  foldGbpIntoEurSupplierCharts,
} from '../utils/annualSummaryCharts';
import {
  buildFamilyTransferChartRows,
  buildFamilyTransfersView,
  familyTransferAccordionKey,
} from '../utils/familyTransfers';
import { LAYOUT_MAIN_SCROLL_ID } from '../components/BackToTopFab';
import {
  filterRowsByChartCategory,
  filterRowsByChartSupplierAndCategory,
  chartColumnLabelFromBarClick,
  toggleChartSupplierFilter,
  toggleChartCategoryFilter,
  type ChartSupplierFilter,
  type ChartCategoryFilter,
} from '../utils/chartTableFilters';
import {
  TAX_RETURN_FILTER_BAR_TEST_ID,
  scrollSectionBelowStickyBar,
} from '../utils/scrollSectionBelowSticky';
import {
  FIT_FLEX_CELL_SX,
  clampFitColPx,
  compactColSx,
  estimateFitTextPx,
  fitContentColSx,
} from '../utils/fitTable';
import {
  whoChipSx,
  whoInitialsFromCardholder,
  whoTooltipLabel,
  type WhoInitials,
} from '../utils/whoChip';

type DocKind = 'receipt' | 'salesInvoice' | 'purchase';
const VAT_SUMMARY_SECTION_KEY = 'summary';
const FAMILY_TRANSFERS_SECTION_KEY = 'family_transfers';
const VAT_DETAIL_SECTION_ID_PREFIX = 'vat-detail-';

const vatDetailSectionId = (key: string) => `${VAT_DETAIL_SECTION_ID_PREFIX}${key}`;

/** Keep Select menus clickable inside the non-blocking side panel (same pattern as Tx Review Quickview). */
const DETAIL_DRAWER_SELECT_MENU_PROPS = { disablePortal: true } as const;
const DETAIL_PANEL_WIDTH_PX = 440;
type CommittedFilter = 'all' | 'only' | 'exclude';
/** ``all``, ``complete`` (No Tags Missing), or an exact Applied status label. */
type TagsFilter = 'all' | 'complete' | string;
type RegionFilter = 'ire' | 'uk';
type RevolutStatusFilter = 'completed' | 'all';
/** ``all`` or a 3-letter ISO currency code (default EUR). */
type OriginalCurrencyFilter = 'all' | string;
/** Default ``unclaimed`` so the page focuses on reclaimable-but-not-yet-claimed TXs. */
type VatClaimedFilter = 'unclaimed' | 'claimed' | 'all';
/** Annual Returns: restrict TX tables to Income or Expenses (from P/L chart clicks). */
type CashflowSideFilter = 'all' | 'in' | 'out';
type BimonthPeriod = 0 | 1 | 2 | 3 | 4 | 5;

/** Rotate category/supplier text only when there are too many columns to fit horizontally. */
const DENSE_COLUMN_MIN_COUNT = 8;
const SPARSE_COLUMN_X_AXIS_HEIGHT = 40;
const SPARSE_COLUMN_CHART_MARGIN = { top: 36, right: 12, left: 0, bottom: 8 } as const;
const DENSE_COLUMN_CHART_MARGIN = { top: 80, right: 12, left: 0, bottom: 8 } as const;

type DenseColumnXAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
};

function DenseColumnXAxisTick({ x = 0, y = 0, payload }: DenseColumnXAxisTickProps) {
  const label = String(payload?.value ?? '');
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        transform="rotate(-90)"
        textAnchor="end"
        fill="#475569"
        fontSize={10}
        x={-6}
        y={3}
      >
        <title>{label}</title>
        {label}
      </text>
    </g>
  );
}

function isDenseColumnChart(columnCount: number): boolean {
  return columnCount >= DENSE_COLUMN_MIN_COUNT;
}

function denseColumnXAxisHeight(labels: Array<string | number | undefined>): number {
  const maxLen = Math.max(4, ...labels.map((label) => String(label ?? '').length));
  return Math.max(64, Math.min(110, 14 + maxLen * 5.5));
}

function columnChartAxisHeight(
  labels: Array<string | number | undefined>,
  dense: boolean
): number {
  return dense ? denseColumnXAxisHeight(labels) : SPARSE_COLUMN_X_AXIS_HEIGHT;
}

function columnChartMargin(dense: boolean): { top: number; right: number; left: number; bottom: number } {
  return dense ? DENSE_COLUMN_CHART_MARGIN : SPARSE_COLUMN_CHART_MARGIN;
}

function columnChartHeight(axisHeight: number, dense: boolean, plotHeight = 220): number {
  const margin = columnChartMargin(dense);
  return margin.top + margin.bottom + axisHeight + plotHeight;
}

function CategoryColumnXAxis({
  dataKey,
  labels,
  dense,
}: {
  dataKey: string;
  labels: Array<string | number | undefined>;
  dense: boolean;
}) {
  if (dense) {
    return (
      <XAxis
        dataKey={dataKey}
        interval={0}
        minTickGap={0}
        height={denseColumnXAxisHeight(labels)}
        tick={DenseColumnXAxisTick}
        tickLine={false}
      />
    );
  }
  return (
    <XAxis dataKey={dataKey} interval={0} height={SPARSE_COLUMN_X_AXIS_HEIGHT} tick={{ fontSize: 12 }} />
  );
}

/** Amount labels sit just outside the bar tip so 90° text does not overlap the column. */
function OutsideColumnValueLabel({
  value,
  viewBox,
  x,
  y,
  width,
  height,
  dense,
  format,
  hideZero = true,
}: {
  value?: number | string | Array<number | string>;
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  dense: boolean;
  format: (n: number) => string;
  hideZero?: boolean;
}) {
  const raw = Array.isArray(value) ? value[0] : value;
  const n = Number(raw);
  if (!Number.isFinite(n) || (hideZero && Math.round(n) === 0)) return null;
  const boxX = Number(viewBox?.x ?? x);
  const boxY = Number(viewBox?.y ?? y);
  const boxW = Number(viewBox?.width ?? width);
  const boxH = Number(viewBox?.height ?? height);
  if (![boxX, boxY, boxW, boxH].every((v) => Number.isFinite(v))) return null;
  const cx = boxX + boxW / 2;
  const top = boxH >= 0 ? boxY : boxY + boxH;
  const bottom = boxH >= 0 ? boxY + boxH : boxY;
  const isNegative = n < 0 || boxH < 0;
  const gap = 8;
  const tipY = isNegative ? bottom + gap : top - gap;
  const label = format(n);
  if (!dense) {
    return (
      <text
        x={cx}
        y={tipY}
        textAnchor="middle"
        dominantBaseline={isNegative ? 'hanging' : 'auto'}
        fill="#334155"
        fontSize={11}
        fontWeight={600}
        pointerEvents="none"
      >
        {label}
      </text>
    );
  }
  return (
    <text
      x={cx}
      y={tipY}
      transform={`rotate(-90 ${cx} ${tipY})`}
      textAnchor={isNegative ? 'end' : 'start'}
      dominantBaseline="middle"
      fill="#334155"
      fontSize={10}
      fontWeight={600}
      pointerEvents="none"
    >
      {label}
    </text>
  );
}

const ALL_TAGS_STATUS = 'No Tags Missing';
const VAT_CONFLICT_STATUS = 'VAT Conflict';
const DEFAULT_ORIGINAL_CURRENCY = 'EUR';

/** Same dimension order as GET /review-rows Applied status labels. */
const TAG_MISSING_DIMS = ['Cash flow', 'Supplier', 'Category', 'VAT Rate'] as const;

/** Format missing-dimension subsets like the backend ``_review_row_status_label``. */
const formatMissingTagsStatus = (dims: string[]): string => {
  if (dims.length === 0) return ALL_TAGS_STATUS;
  if (dims.length === 1) return `${dims[0]} Missing`;
  if (dims.length === 2) return `${dims[0]} & ${dims[1]} Missing`;
  if (dims.length === 3) return `${dims[0]}, ${dims[1]} & ${dims[2]} Missing`;
  return `${dims[0]}, ${dims[1]}, ${dims[2]} & ${dims[3]} Missing`;
};

const countMissingDimsInStatus = (status: string): number =>
  TAG_MISSING_DIMS.filter((dim) => status.includes(dim)).length;

/** All non-empty missing-dimension permutations (15), ordered by size then label. */
const TAG_MISSING_STATUS_PERMUTATIONS: string[] = (() => {
  const dims = [...TAG_MISSING_DIMS];
  const out: string[] = [];
  const n = dims.length;
  for (let mask = 1; mask < 1 << n; mask += 1) {
    const subset: string[] = [];
    for (let i = 0; i < n; i += 1) {
      if (mask & (1 << i)) subset.push(dims[i]);
    }
    out.push(formatMissingTagsStatus(subset));
  }
  out.sort((a, b) => {
    const ca = countMissingDimsInStatus(a);
    const cb = countMissingDimsInStatus(b);
    if (ca !== cb) return ca - cb;
    return a.localeCompare(b);
  });
  return out;
})();

/** Tag-missing labels used on Annual Returns (omit VAT Rate / VAT Conflict). */
const TAG_MISSING_STATUS_ANNUAL: string[] = TAG_MISSING_STATUS_PERMUTATIONS.filter(
  (status) => !status.includes('VAT Rate')
);

const VAT_URL_KEYS = {
  year: 'year',
  period: 'period',
  region: 'region',
  committed: 'committed',
  tags: 'tags',
  revolutStatus: 'revolut_status',
  originalCurrency: 'original_currency',
  hideZeroVat: 'hide_zero_vat',
  vatClaimed: 'vat_claimed',
  side: 'side',
  cashSide: 'cash_side',
  chartSupplier: 'chart_supplier',
  chartSection: 'chart_section',
} as const;

const BIMONTH_PERIODS: Array<{ value: BimonthPeriod; label: string }> = [
  { value: 0, label: 'Jan – Feb' },
  { value: 1, label: 'Mar – Apr' },
  { value: 2, label: 'May – Jun' },
  { value: 3, label: 'Jul – Aug' },
  { value: 4, label: 'Sep – Oct' },
  { value: 5, label: 'Nov – Dec' },
];

const BIMONTH_PERIOD_URL: Record<BimonthPeriod, string> = {
  0: 'jan-feb',
  1: 'mar-apr',
  2: 'may-jun',
  3: 'jul-aug',
  4: 'sep-oct',
  5: 'nov-dec',
};

/** Full month names for accordion titles, e.g. "May & June 2026". */
const BIMONTH_RANGE_MONTHS: Record<BimonthPeriod, [string, string]> = {
  0: ['January', 'February'],
  1: ['March', 'April'],
  2: ['May', 'June'],
  3: ['July', 'August'],
  4: ['September', 'October'],
  5: ['November', 'December'],
};

const bimonthRangeLabel = (period: BimonthPeriod, year: number): string => {
  const [first, second] = BIMONTH_RANGE_MONTHS[period];
  return `${first} & ${second} ${year}`;
};

/** Inclusive ISO date window for one bi-month (matches API review-rows date_from/date_to). */
const bimonthDateWindow = (
  period: BimonthPeriod,
  year: number
): { dateFrom: string; dateTo: string } => {
  const startMonth = period * 2; // 0-based
  const endMonth = startMonth + 1;
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = new Date(year, endMonth + 1, 0).getDate();
  return {
    dateFrom: `${year}-${pad(startMonth + 1)}-01`,
    dateTo: `${year}-${pad(endMonth + 1)}-${pad(lastDay)}`,
  };
};

const bimonthForMonth = (monthIndex: number): BimonthPeriod =>
  Math.floor(monthIndex / 2) as BimonthPeriod;

const currentYear = () => new Date().getFullYear();
const currentBimonth = (): BimonthPeriod => bimonthForMonth(new Date().getMonth());

export type TaxReturnPageVariant = 'vat' | 'annual';

/** Inclusive ISO date window for a full calendar year. */
const yearDateWindow = (year: number): { dateFrom: string; dateTo: string } => ({
  dateFrom: `${year}-01-01`,
  dateTo: `${year}-12-31`,
});

const REVIEW_ROWS_PAGE_SIZE = 2000;
const REVIEW_ROWS_MAX_PAGES = 15;

type VatUrlFilters = {
  year: number;
  period: BimonthPeriod;
  region: RegionFilter;
  committed: CommittedFilter;
  tags: TagsFilter;
  revolutStatus: RevolutStatusFilter;
  originalCurrency: OriginalCurrencyFilter;
  /** When true, hide TXs tagged at literal 0% VAT (default On). */
  hideZeroVat: boolean;
  /** Default ``unclaimed``. */
  vatClaimed: VatClaimedFilter;
  side: CashflowSideFilter;
  cashSide: CashflowSideFilter;
  chartSupplier: string;
  chartSection: string;
};

const parseCashflowSide = (raw: string | null): CashflowSideFilter => {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'in' || s === 'income') return 'in';
  if (s === 'out' || s === 'expense' || s === 'expenses') return 'out';
  return 'all';
};

const cashflowSideToUrl = (side: CashflowSideFilter): string =>
  side === 'in' ? 'income' : side === 'out' ? 'expenses' : 'all';

const parseBimonthPeriod = (raw: string | null): BimonthPeriod | null => {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  const byLabel = (Object.entries(BIMONTH_PERIOD_URL) as Array<[string, string]>).find(([, value]) => value === s);
  if (byLabel) return Number(byLabel[0]) as BimonthPeriod;
  const asNumber = Number(s);
  if (Number.isInteger(asNumber) && asNumber >= 0 && asNumber <= 5) {
    return asNumber as BimonthPeriod;
  }
  return null;
};

const parseVatFiltersFromSearch = (
  params: URLSearchParams,
  variant: TaxReturnPageVariant = 'vat'
): VatUrlFilters => {
  const isAnnual = variant === 'annual';
  const yearRaw = params.get(VAT_URL_KEYS.year);
  const yearNum = yearRaw ? Number(yearRaw) : NaN;
  const year = Number.isInteger(yearNum) && yearNum >= 2000 && yearNum <= 2100
    ? yearNum
    : currentYear();

  const period = isAnnual
    ? (0 as BimonthPeriod)
    : parseBimonthPeriod(params.get(VAT_URL_KEYS.period)) ?? currentBimonth();

  const regionRaw = String(params.get(VAT_URL_KEYS.region) || '').trim().toLowerCase();
  const region: RegionFilter =
    regionRaw === 'uk' || regionRaw === 'ire' || regionRaw === 'ie' ? (regionRaw === 'uk' ? 'uk' : 'ire') : 'ire';

  const committedRaw = String(params.get(VAT_URL_KEYS.committed) || '').trim().toLowerCase();
  const committed: CommittedFilter =
    committedRaw === 'only' || committedRaw === 'exclude' || committedRaw === 'all'
      ? committedRaw
      : 'all';

  const tagsRaw = String(params.get(VAT_URL_KEYS.tags) || '').trim();
  const tagsLower = tagsRaw.toLowerCase();
  let tags: TagsFilter = 'all';
  if (!tagsRaw || tagsLower === 'all') {
    tags = 'all';
  } else if (tagsLower === 'complete') {
    tags = 'complete';
  } else if (
    tagsRaw === VAT_CONFLICT_STATUS ||
    tagsRaw === ALL_TAGS_STATUS ||
    TAG_MISSING_STATUS_PERMUTATIONS.includes(tagsRaw)
  ) {
    tags = tagsRaw;
  } else {
    // Allow deep-linked Applied labels that may appear in data.
    tags = tagsRaw;
  }
  if (
    isAnnual
    && (
      tags === VAT_CONFLICT_STATUS
      || (tags !== 'all' && tags !== 'complete' && String(tags).includes('VAT Rate'))
    )
  ) {
    tags = 'all';
  }

  const revolutStatusRaw = String(params.get(VAT_URL_KEYS.revolutStatus) || '').trim().toLowerCase();
  const revolutStatus: RevolutStatusFilter =
    revolutStatusRaw === 'all' || revolutStatusRaw === 'completed' ? revolutStatusRaw : 'completed';

  const currencyRaw = String(params.get(VAT_URL_KEYS.originalCurrency) || '').trim().toUpperCase();
  let originalCurrency: OriginalCurrencyFilter =
    currencyRaw === 'ALL' || (isAnnual && currencyRaw === '')
      ? 'all'
      : /^[A-Z]{3}$/.test(currencyRaw)
        ? currencyRaw
        : isAnnual
          ? 'all'
          : DEFAULT_ORIGINAL_CURRENCY;
  // Previous Annual default wrote EUR into the URL; that hides all UK GBP income.
  if (isAnnual && region === 'uk' && originalCurrency === 'EUR') {
    originalCurrency = 'all';
  }

  const hideZeroRaw = String(params.get(VAT_URL_KEYS.hideZeroVat) || '').trim().toLowerCase();
  // VAT default On (reclaim focus). Annual Returns default Off (full-year P&L).
  const hideZeroVat =
    hideZeroRaw === ''
      ? !isAnnual
      : !['0', 'false', 'off', 'no'].includes(hideZeroRaw);

  const vatClaimedRaw = String(params.get(VAT_URL_KEYS.vatClaimed) || '').trim().toLowerCase();
  const vatClaimed: VatClaimedFilter =
    vatClaimedRaw === 'claimed' || vatClaimedRaw === 'all' || vatClaimedRaw === 'unclaimed'
      ? vatClaimedRaw
      : isAnnual
        ? 'all'
        : 'unclaimed';

  const side = isAnnual ? parseCashflowSide(params.get(VAT_URL_KEYS.side)) : 'all';
  const cashSide = isAnnual ? parseCashflowSide(params.get(VAT_URL_KEYS.cashSide)) : 'all';
  const chartSupplier = String(params.get(VAT_URL_KEYS.chartSupplier) || '').trim();
  const chartSection = String(params.get(VAT_URL_KEYS.chartSection) || '').trim();

  return {
    year,
    period,
    region,
    committed,
    tags,
    revolutStatus,
    originalCurrency,
    hideZeroVat,
    vatClaimed,
    side,
    cashSide,
    chartSupplier,
    chartSection,
  };
};

const writeVatFiltersToSearch = (
  params: URLSearchParams,
  filters: VatUrlFilters,
  variant: TaxReturnPageVariant = 'vat'
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set(VAT_URL_KEYS.year, String(filters.year));
  if (variant === 'annual') {
    next.delete(VAT_URL_KEYS.period);
    next.delete(VAT_URL_KEYS.hideZeroVat);
    next.delete(VAT_URL_KEYS.vatClaimed);
    if (filters.side === 'all') next.delete(VAT_URL_KEYS.side);
    else next.set(VAT_URL_KEYS.side, cashflowSideToUrl(filters.side));
    if (filters.cashSide === 'all') next.delete(VAT_URL_KEYS.cashSide);
    else next.set(VAT_URL_KEYS.cashSide, filters.cashSide);
  } else {
    next.set(VAT_URL_KEYS.period, BIMONTH_PERIOD_URL[filters.period]);
    next.set(VAT_URL_KEYS.hideZeroVat, filters.hideZeroVat ? '1' : '0');
    next.set(VAT_URL_KEYS.vatClaimed, filters.vatClaimed);
    next.delete(VAT_URL_KEYS.side);
    next.delete(VAT_URL_KEYS.cashSide);
  }
  next.set(VAT_URL_KEYS.region, filters.region);
  next.set(VAT_URL_KEYS.committed, filters.committed);
  next.set(VAT_URL_KEYS.tags, filters.tags);
  next.set(VAT_URL_KEYS.revolutStatus, filters.revolutStatus);
  next.set(VAT_URL_KEYS.originalCurrency, filters.originalCurrency);
  if (!filters.chartSupplier || !filters.chartSection) {
    next.delete(VAT_URL_KEYS.chartSupplier);
    next.delete(VAT_URL_KEYS.chartSection);
  } else {
    next.set(VAT_URL_KEYS.chartSupplier, filters.chartSupplier);
    next.set(VAT_URL_KEYS.chartSection, filters.chartSection);
  }
  return next;
};

/** Canonical VAT bands shown for each region (plus any extra rates found in the data). */
const REGION_VAT_BANDS: Record<'IRE' | 'UK', string[]> = {
  IRE: ['0%', '9%', '13.5%', '21%', '23%'],
  UK: ['0%', '5%', '20%'],
};

/** Per-TX table columns: VAT amount broken out by rate for the active region tab. */
const REGION_VAT_RATE_COLUMNS: Record<RegionFilter, string[]> = {
  ire: ['0%', '9%', '13.5%', '23%'],
  uk: ['0%', '5%', '20%'],
};

/**
 * Classification Review column policy on VAT / Annual Returns TX tables:
 * compact cols are content-fitted px (never ellipsis); Description/Supplier/Category share leftover.
 * Table is always width 100% with table-layout:fixed — no horizontal scroll.
 */
const VAT_TX_TABLE_SX = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  tableLayout: 'fixed' as const,
  '& .MuiTableCell-root': {
    boxSizing: 'border-box' as const,
    px: '4px',
    py: 0.45,
    fontSize: '0.8125rem',
    verticalAlign: 'middle',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    textAlign: 'center',
    whiteSpace: 'normal',
    lineHeight: 1.15,
    verticalAlign: 'bottom',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root': {
    justifyContent: 'center',
    alignItems: 'flex-end',
    width: '100%',
    color: 'inherit',
    height: 'auto',
    whiteSpace: 'normal',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-root.Mui-active': {
    color: 'inherit',
  },
  '& .MuiTableHead-root .MuiTableSortLabel-icon': {
    position: 'static',
    marginLeft: '2px',
    marginBottom: '2px',
    opacity: 0.7,
  },
};

/** Multi-line header label (``\\n`` → line break). */
const vatTxHeaderLabel = (label: string) => (
  <Box
    component="span"
    sx={{
      display: 'block',
      textAlign: 'center',
      whiteSpace: 'pre-line',
      lineHeight: 1.15,
      fontWeight: 700,
    }}
  >
    {label}
  </Box>
);

const vatTxBandHeaderLabel = (band: string) => vatTxHeaderLabel(`VAT\n@ ${band}`);

const vatTxTruncateSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: '100%',
};

const VAT_TX_ACTION_BTN_PX = 28;
const VAT_TX_ACTION_ICON_COUNT_VAT = 6;
const VAT_TX_ACTION_ICON_COUNT_ANNUAL = 4;
/** Extra px on Total VAT so right-aligned amounts stay clear of the first Actions icon. */
const VAT_TX_TOTAL_VAT_EXTRA_PX = 20;
const VAT_TX_COMPACT = {
  checkbox: 36,
  who: 48,
  actionsVat: VAT_TX_ACTION_ICON_COUNT_VAT * VAT_TX_ACTION_BTN_PX,
  actionsAnnual: VAT_TX_ACTION_ICON_COUNT_ANNUAL * VAT_TX_ACTION_BTN_PX,
} as const;
const VAT_TX_ACTION_BTN_SX = {
  width: VAT_TX_ACTION_BTN_PX,
  height: VAT_TX_ACTION_BTN_PX,
  p: 0.25,
} as const;

const estimateVatHeaderPx = (label: string): number => {
  const lines = String(label).split('\n');
  const widest = lines.reduce((max, line) => Math.max(max, estimateFitTextPx(line)), 0);
  return widest + 18;
};

const formatVatMoneyLabel = (amount: number, currency?: string | null): string => {
  const ccy = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${ccy}`;
  }
};

type VatTxFitWidths = {
  id: number;
  date: number;
  ccy: number;
  rate: number;
  total: number;
  net: number;
  vat: number;
  band: number;
};

interface VatBandTotals {
  band: string;
  ratePct: number;
  gross: number;
  net: number;
  vat: number;
  count: number;
}

interface RegionVatSummary {
  region: 'IRE' | 'UK';
  currency: string;
  gross: number;
  net: number;
  vat: number;
  count: number;
  bands: VatBandTotals[];
}

interface VatTagChip {
  uid?: string | null;
  source?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  category?: { id?: number | null; name?: string | null } | null;
  supplier_name?: string | null;
  vat_rate?: string | null;
  pl_class?: string | null;
}

interface VatRow {
  id: number;
  revolut_id?: string | null;
  revolut_state?: string | null;
  status?: string | null;
  account_source?: string | null;
  description?: string | null;
  amount?: number | null;
  currency?: string | null;
  bill_currency?: string | null;
  vat_rate?: string | null;
  date?: string | null;
  cashFlow?: string | null;
  category?: string | null;
  pl_class?: string | null;
  supplier?: string | null;
  tags?: VatTagChip[] | null;
  property_id?: number | null;
  property_ids?: number[] | null;
  property_nickname?: string | null;
  property_nicknames?: string[] | null;
  properties?: Array<{ id?: number | null; nickname?: string | null }> | null;
  transaction?: {
    bill_currency?: string | null;
    currency?: string | null;
    card_data?: unknown;
    card?: unknown;
    cardholder?: string | null;
    card_holder?: string | null;
  } | null;
  vat_claim_status?: string | null;
  comment_internal?: string | null;
  comment_external?: string | null;
  flags?: { is_committed?: boolean; vat_claimed?: boolean } | null;
  [key: string]: unknown;
}

const isVatClaimed = (row: VatRow): boolean => {
  if (row.flags?.vat_claimed === true) return true;
  return String(row.vat_claim_status || '').trim().toLowerCase() === 'claimed';
};

const fetchVatCompactReviewRows = async (params: {
  committed: CommittedFilter;
  dateFrom: string;
  dateTo: string;
  includeDeclined: boolean;
}): Promise<{ rows: VatRow[]; truncated: boolean }> => {
  const all: VatRow[] = [];
  let offset = 0;
  let truncated = false;
  for (let page = 0; page < REVIEW_ROWS_MAX_PAGES; page += 1) {
    const res = await api.get('/api/transaction-classification-rules/review-rows', {
      params: {
        committed: params.committed,
        limit: REVIEW_ROWS_PAGE_SIZE,
        offset,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        fields: 'vat_compact',
        include_declined: params.includeDeclined ? 'true' : 'false',
      },
    });
    const nextRows: VatRow[] = Array.isArray(res.data?.rows) ? res.data.rows : [];
    all.push(...nextRows);
    const countTotal = Number(res.data?.count_total);
    const shortPage = nextRows.length < REVIEW_ROWS_PAGE_SIZE;
    const haveAll = Number.isFinite(countTotal) && all.length >= countTotal;
    if (shortPage || haveAll) {
      truncated = Number.isFinite(countTotal) && countTotal > all.length;
      break;
    }
    offset += nextRows.length;
    if (page === REVIEW_ROWS_MAX_PAGES - 1) {
      truncated = !Number.isFinite(countTotal) || all.length < countTotal;
    }
  }
  return { rows: all, truncated };
};

type VatCategoryOption = { id: number; name: string; pl_class?: string };
type VatPropOption = { id: number; nickname: string };

const TX_PROP_NICKNAMES = ['2SA', '28LC', '87HP', '67RR'] as const;
const VAT_RATE_FALLBACK_OPTIONS = ['23%', '21%', '20%', '13.5%', '9%', '5%', '0%', 'exempt'];

/** Cash-flow heading categories (`In` / `Out` / `N/A`; legacy Income/Expense during transition). */
const isTopLevelCashflowHeadingName = (name: string): boolean => {
  const n = (name || '').trim().toLowerCase();
  return n === 'in' || n === 'out' || n === 'n/a' || n === 'na' || n === 'income' || n === 'expense';
};

const canonicalizeVatRate = (value: string | null | undefined): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (lowered === 'exempt') return 'exempt';
  if (lowered === 'none' || lowered === 'n/a' || lowered === 'na') return '';
  const numericRaw = raw.endsWith('%') ? raw.slice(0, -1).trim() : raw;
  if (/^-?\d+(\.\d+)?$/.test(numericRaw)) {
    const n = Number(numericRaw);
    if (Number.isFinite(n)) return `${String(Number(numericRaw))}%`;
  }
  return raw;
};

const tagCategoryName = (tag: VatTagChip): string =>
  String(tag.category_name || tag.category?.name || '').trim();

const tagCategoryId = (tag: VatTagChip): number | null => {
  const fromTag = Number(tag.category_id ?? tag.category?.id);
  if (Number.isFinite(fromTag) && fromTag > 0) return fromTag;
  return null;
};

const rowPropertyIds = (row: VatRow): number[] => {
  const fromIds = Array.isArray(row.property_ids)
    ? row.property_ids.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (fromIds.length) return Array.from(new Set(fromIds));
  const fromProps = Array.isArray(row.properties)
    ? row.properties.map((p) => Number(p?.id)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (fromProps.length) return Array.from(new Set(fromProps));
  const single = Number(row.property_id);
  return Number.isFinite(single) && single > 0 ? [single] : [];
};

async function postManualRevolutCategoryTagIgnoreDuplicate(body: {
  revolut_transaction_id: number;
  category_id: number;
  source: string;
  vat_rate?: string;
  supplier_name?: string;
}): Promise<void> {
  try {
    await api.post('/api/revolut-transaction-category-tags/', body);
  } catch (err: any) {
    const status = err?.response?.status;
    const msg = String(err?.response?.data?.error || err?.message || '').toLowerCase();
    if (status === 400 && msg.includes('already exists')) {
      await api.post('/api/revolut-transaction-category-tags/', body);
      return;
    }
    throw err;
  }
}

/** Set canonical VAT via tx_vat_tags (does not write legacy category-tag vat_rate). */
async function setManualTxVatRate(txId: number, vatRate: string): Promise<void> {
  await api.post('/api/revolut-transaction-category-tags/set-vat', {
    revolut_transaction_id: txId,
    vat_rate: vatRate,
  });
}

const formatCategory = (row: VatRow): string => {
  const s = String(row.category ?? '').trim();
  if (!s || s === '—' || s === '-') return '—';
  return s;
};

const formatSupplier = (row: VatRow): string => {
  const s = String(row.supplier ?? '').trim();
  if (!s || s === '—' || s === '-') return '—';
  return s;
};

interface CategoryGrossLine {
  category: string;
  count: number;
  gross: number;
}

interface CurrencyCategorySummary {
  currency: string;
  lines: CategoryGrossLine[];
  count: number;
  gross: number;
}

/** Per-currency category totals (absolute gross) for Annual Returns P&L tables. */
const buildCategoryGrossSummaries = (rows: VatRow[]): CurrencyCategorySummary[] => {
  const byCurrency = new Map<string, Map<string, CategoryGrossLine>>();
  rows.forEach((row) => {
    const currency = String(row.currency || 'EUR').trim().toUpperCase() || 'EUR';
    const category = annualSummaryChartCategory(formatCategory(row));
    const grossAbs = Math.abs(Number(row.amount || 0));
    if (!Number.isFinite(grossAbs)) return;
    let cats = byCurrency.get(currency);
    if (!cats) {
      cats = new Map();
      byCurrency.set(currency, cats);
    }
    const existing = cats.get(category);
    if (existing) {
      existing.count += 1;
      existing.gross += grossAbs;
    } else {
      cats.set(category, { category, count: 1, gross: grossAbs });
    }
  });
  return Array.from(byCurrency.entries())
    .map(([currency, cats]) => {
      const lines = Array.from(cats.values()).sort(
        (a, b) => b.gross - a.gross || a.category.localeCompare(b.category)
      );
      return {
        currency,
        lines,
        count: lines.reduce((n, line) => n + line.count, 0),
        gross: lines.reduce((n, line) => n + line.gross, 0),
      };
    })
    .sort((a, b) => a.currency.localeCompare(b.currency));
};

const isRowCommitted = (row: VatRow): boolean => Boolean(row.flags?.is_committed);
const hasAllTags = (row: VatRow): boolean => String(row.status || '').trim() === ALL_TAGS_STATUS;

const rowMatchesTagsFilter = (row: VatRow, filter: TagsFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'complete') return hasAllTags(row);
  return String(row.status || '').trim() === filter;
};

/** Normalized cash-flow bucket for sectioning the VAT TX table. */
const cashflowBucket = (row: VatRow): 'in' | 'out' | 'other' => {
  const s = String(row.cashFlow ?? '').trim().toLowerCase();
  if (s === 'in' || s === 'income') return 'in';
  if (s === 'out' || s === 'expense') return 'out';
  // Explicit N/A stays out of In/Out (zero-amount / balance-unchanged, etc.).
  if (s === 'n/a' || s === 'na') return 'other';
  // Missing cash-flow heading ("—"/blank) is common on income TXs; infer from sign.
  const amount = Number(row.amount);
  if (Number.isFinite(amount) && amount !== 0) {
    return amount > 0 ? 'in' : 'out';
  }
  return 'other';
};

type AnnualPlSide = 'Income' | 'Expenses' | 'Profit/Loss' | 'Cash in' | 'Cash out' | 'Net cash';

type AnnualPlSideRow = {
  side: AnnualPlSide;
  Gross: number;
  labelTotal: number;
};

type AnnualPlCurrencyChart = {
  currency: string;
  sideRows: AnnualPlSideRow[];
};

const buildAnnualAbsChartsFromRows = (
  inputRows: VatRow[],
  labels: { inLabel: AnnualPlSide; outLabel: AnnualPlSide; netLabel: AnnualPlSide }
): AnnualPlCurrencyChart[] => {
  const byCurrency = new Map<string, { income: number; expenses: number }>();
  inputRows.forEach((row) => {
    const bucket = cashflowBucket(row);
    if (bucket !== 'in' && bucket !== 'out') return;
    const currency = String(row.currency || 'EUR').trim().toUpperCase() || 'EUR';
    const grossAbs = Math.abs(Number(row.amount || 0));
    if (!Number.isFinite(grossAbs) || grossAbs <= 0) return;
    const acc = byCurrency.get(currency) || { income: 0, expenses: 0 };
    if (bucket === 'in') acc.income += grossAbs;
    else acc.expenses += grossAbs;
    byCurrency.set(currency, acc);
  });
  const round = (n: number) => Number(n.toFixed(2));
  return Array.from(byCurrency.entries())
    .map(([currency, totals]) => {
      const profit = totals.income - totals.expenses;
      return {
        currency,
        sideRows: [
          { side: labels.inLabel, Gross: round(totals.income), labelTotal: round(totals.income) },
          { side: labels.outLabel, Gross: round(totals.expenses), labelTotal: round(totals.expenses) },
          { side: labels.netLabel, Gross: round(profit), labelTotal: round(profit) },
        ],
      };
    })
    .sort((a, b) => a.currency.localeCompare(b.currency));
};

const buildAnnualPlChartsFromRows = (inputRows: VatRow[]): AnnualPlCurrencyChart[] =>
  buildAnnualAbsChartsFromRows(inputRows, {
    inLabel: 'Income',
    outLabel: 'Expenses',
    netLabel: 'Profit/Loss',
  });

const buildAnnualCashChartsFromRows = (inputRows: VatRow[]): AnnualPlCurrencyChart[] =>
  buildAnnualAbsChartsFromRows(inputRows, {
    inLabel: 'Cash in',
    outLabel: 'Cash out',
    netLabel: 'Net cash',
  });

/** Original merchant currency (bill_currency) when present; otherwise ledger currency. */
const originalCurrencyForRow = (row: VatRow): string => {
  const fromBill = String(row.bill_currency || row.transaction?.bill_currency || '')
    .trim()
    .toUpperCase();
  if (fromBill) return fromBill;
  return String(row.currency || row.transaction?.currency || '').trim().toUpperCase();
};

/** Match review-rows include_declined=false: blank or completed Revolut state. */
const isRevolutCompletedOrBlank = (row: VatRow): boolean => {
  const s = String(row.revolut_state || '').trim().toLowerCase();
  return !s || s === 'completed';
};

/** Map ledger account_source to IRE / UK (same idea as Tx Review region labels). */
const regionForRow = (row: VatRow): 'IRE' | 'UK' | null => {
  const s = String(row.account_source || '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('uk')) return 'UK';
  if (s.includes('ireland') || s.includes('ire')) return 'IRE';
  return null;
};

const formatRegion = (row: VatRow): string => regionForRow(row) || '-';

/** Normalize tag VAT strings like "20%|uk", "13.5%", "exempt" into a band label. */
const normalizeVatBand = (raw: unknown): { band: string; ratePct: number } => {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s || s === '—' || s === '-' || s === 'n/a' || s === 'na') {
    return { band: 'Unspecified', ratePct: 0 };
  }
  if (s.includes('exempt')) {
    return { band: 'Exempt', ratePct: 0 };
  }
  const match = s.match(/([\d.]+)/);
  if (!match) {
    return { band: 'Unspecified', ratePct: 0 };
  }
  const ratePct = Number(match[1]);
  if (!Number.isFinite(ratePct)) {
    return { band: 'Unspecified', ratePct: 0 };
  }
  const band = Number.isInteger(ratePct) ? `${ratePct}%` : `${ratePct}%`;
  return { band, ratePct };
};

/** True when the TX is tagged at a literal 0% VAT rate (not exempt / unspecified). */
const isZeroPercentVatRateRow = (row: VatRow): boolean => normalizeVatBand(row.vat_rate).band === '0%';

/** Bank amounts are treated as gross (incl. VAT); split using the band rate. */
const splitGrossVat = (grossAbs: number, ratePct: number): { net: number; vat: number } => {
  if (!(grossAbs > 0) || !(ratePct > 0)) {
    return { net: grossAbs, vat: 0 };
  }
  const net = grossAbs / (1 + ratePct / 100);
  const vat = grossAbs - net;
  return { net, vat };
};

/** Split ledger amount (gross incl. VAT) into net + VAT, keeping the TX sign. */
const rowAmountBreakdown = (row: VatRow): { total: number; net: number; vat: number } => {
  const total = Number(row.amount || 0);
  if (!Number.isFinite(total) || total === 0) {
    return { total: Number.isFinite(total) ? total : 0, net: 0, vat: 0 };
  }
  const { ratePct } = normalizeVatBand(row.vat_rate);
  const abs = Math.abs(total);
  const { net: netAbs, vat: vatAbs } = splitGrossVat(abs, ratePct);
  const sign = total < 0 ? -1 : 1;
  return { total, net: netAbs * sign, vat: vatAbs * sign };
};

/** VAT amount for a TX only when its tagged rate matches ``band``; otherwise null. */
const rowVatAmountForBand = (row: VatRow, band: string): number | null => {
  const { band: rowBand } = normalizeVatBand(row.vat_rate);
  if (rowBand !== band) return null;
  return rowAmountBreakdown(row).vat;
};

type VatTxCurrencyTotals = {
  currency: string;
  count: number;
  total: number;
  net: number;
  vat: number;
  bandVat: Record<string, number>;
};

/** Sum displayed TX amount columns, one total row per ledger currency. */
const buildVatTxTableTotals = (rows: VatRow[], bands: string[]): VatTxCurrencyTotals[] => {
  const byCcy = new Map<string, VatTxCurrencyTotals>();
  for (const row of rows) {
    const currency = String(row.currency || 'EUR').trim().toUpperCase() || 'EUR';
    let acc = byCcy.get(currency);
    if (!acc) {
      acc = {
        currency,
        count: 0,
        total: 0,
        net: 0,
        vat: 0,
        bandVat: Object.fromEntries(bands.map((band) => [band, 0])),
      };
      byCcy.set(currency, acc);
    }
    const amounts = rowAmountBreakdown(row);
    acc.count += 1;
    acc.total += amounts.total;
    acc.net += amounts.net;
    acc.vat += amounts.vat;
    for (const band of bands) {
      const bandVat = rowVatAmountForBand(row, band);
      if (bandVat != null) acc.bandVat[band] += bandVat;
    }
  }
  return Array.from(byCcy.values()).sort((a, b) => a.currency.localeCompare(b.currency));
};

const buildRegionVatSummaries = (inputRows: VatRow[]): RegionVatSummary[] => {
  type AccBand = { ratePct: number; gross: number; net: number; vat: number; count: number };
  type AccRegion = {
    region: 'IRE' | 'UK';
    currency: string;
    gross: number;
    net: number;
    vat: number;
    count: number;
    bands: Map<string, AccBand>;
  };

  const groups = new Map<string, AccRegion>();

  inputRows.forEach((row) => {
    const region = regionForRow(row);
    if (!region) return;
    const currency = String(row.currency || (region === 'UK' ? 'GBP' : 'EUR')).toUpperCase();
    const key = `${region}:${currency}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        region,
        currency,
        gross: 0,
        net: 0,
        vat: 0,
        count: 0,
        bands: new Map(),
      };
      groups.set(key, group);
    }

    const grossAbs = Math.abs(Number(row.amount || 0));
    if (!Number.isFinite(grossAbs) || grossAbs <= 0) return;

    const { band, ratePct } = normalizeVatBand(row.vat_rate);
    const { net, vat } = splitGrossVat(grossAbs, ratePct);

    group.gross += grossAbs;
    group.net += net;
    group.vat += vat;
    group.count += 1;

    const existing = group.bands.get(band);
    if (existing) {
      existing.gross += grossAbs;
      existing.net += net;
      existing.vat += vat;
      existing.count += 1;
    } else {
      group.bands.set(band, { ratePct, gross: grossAbs, net, vat, count: 1 });
    }
  });

  const orderBand = (region: 'IRE' | 'UK', band: string): number => {
    const canonical = REGION_VAT_BANDS[region];
    const idx = canonical.indexOf(band);
    if (idx >= 0) return idx;
    if (band === 'Exempt') return canonical.length;
    if (band === 'Unspecified') return canonical.length + 1;
    return canonical.length + 2;
  };

  return Array.from(groups.values())
    .sort((a, b) => {
      if (a.region !== b.region) return a.region === 'IRE' ? -1 : 1;
      return a.currency.localeCompare(b.currency);
    })
    .map((group) => {
      const canonical = REGION_VAT_BANDS[group.region];
      const bandNames = new Set<string>([...canonical, ...Array.from(group.bands.keys())]);
      const bands: VatBandTotals[] = Array.from(bandNames)
        .sort((a, b) => {
          const oa = orderBand(group.region, a);
          const ob = orderBand(group.region, b);
          if (oa !== ob) return oa - ob;
          return a.localeCompare(b);
        })
        .filter((band) => {
          if (canonical.includes(band)) return true;
          return (group.bands.get(band)?.count || 0) > 0;
        })
        .map((band) => {
          const existing = group.bands.get(band);
          if (existing) {
            return {
              band,
              ratePct: existing.ratePct,
              gross: existing.gross,
              net: existing.net,
              vat: existing.vat,
              count: existing.count,
            };
          }
          const ratePct = normalizeVatBand(band).ratePct;
          return { band, ratePct, gross: 0, net: 0, vat: 0, count: 0 };
        });

      return {
        region: group.region,
        currency: group.currency,
        gross: group.gross,
        net: group.net,
        vat: group.vat,
        count: group.count,
        bands,
      };
    });
};

const rowDate = (row: VatRow): Date | null => {
  const raw = row.date;
  if (!raw) return null;
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatRowDate = (row: VatRow): string => {
  const d = rowDate(row);
  if (!d) return '-';
  return new Intl.DateTimeFormat('en-IE', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(d);
};

const computeVatTxFitWidths = (rows: VatRow[], bands: string[]): VatTxFitWidths => {
  let idPx = estimateVatHeaderPx('TX\nID');
  let datePx = estimateVatHeaderPx('Date');
  let ccyPx = estimateVatHeaderPx('Orig\nCcy');
  let ratePx = estimateVatHeaderPx('VAT\nrate');
  let totalPx = estimateVatHeaderPx('Gross\nTotal');
  let netPx = estimateVatHeaderPx('Net');
  let vatPx = estimateVatHeaderPx('Total\nVAT');
  let bandPx = bands.reduce(
    (max, band) => Math.max(max, estimateVatHeaderPx(`VAT\n@ ${band}`)),
    56
  );

  for (const row of rows) {
    idPx = Math.max(idPx, estimateFitTextPx(String(row.id)) + 8);
    datePx = Math.max(datePx, estimateFitTextPx(formatRowDate(row)) + 8);
    const ccy = originalCurrencyForRow(row) || '-';
    ccyPx = Math.max(ccyPx, estimateFitTextPx(ccy) + 8);
    ratePx = Math.max(ratePx, estimateFitTextPx(String(row.vat_rate || '-')) + 8);
    const amounts = rowAmountBreakdown(row);
    const moneyCcy = row.currency;
    totalPx = Math.max(totalPx, estimateFitTextPx(formatVatMoneyLabel(amounts.total, moneyCcy)) + 10);
    netPx = Math.max(netPx, estimateFitTextPx(formatVatMoneyLabel(amounts.net, moneyCcy)) + 10);
    vatPx = Math.max(vatPx, estimateFitTextPx(formatVatMoneyLabel(amounts.vat, moneyCcy)) + 10);
    for (const band of bands) {
      const bandVat = rowVatAmountForBand(row, band);
      const label = bandVat == null ? '-' : formatVatMoneyLabel(bandVat, moneyCcy);
      bandPx = Math.max(bandPx, estimateFitTextPx(label) + 10);
    }
  }

  for (const totals of buildVatTxTableTotals(rows, bands)) {
    totalPx = Math.max(totalPx, estimateFitTextPx(formatVatMoneyLabel(totals.total, totals.currency)) + 10);
    netPx = Math.max(netPx, estimateFitTextPx(formatVatMoneyLabel(totals.net, totals.currency)) + 10);
    vatPx = Math.max(vatPx, estimateFitTextPx(formatVatMoneyLabel(totals.vat, totals.currency)) + 10);
    for (const band of bands) {
      bandPx = Math.max(
        bandPx,
        estimateFitTextPx(formatVatMoneyLabel(totals.bandVat[band] || 0, totals.currency)) + 10
      );
    }
  }

  return {
    id: clampFitColPx(idPx, 34, 96),
    date: clampFitColPx(datePx, 72, 120),
    ccy: clampFitColPx(ccyPx, 36, 64),
    rate: clampFitColPx(ratePx, 44, 72),
    total: clampFitColPx(totalPx, 72, 168),
    net: clampFitColPx(netPx, 72, 168),
    vat: clampFitColPx(vatPx + VAT_TX_TOTAL_VAT_EXTRA_PX, 88, 188),
    band: clampFitColPx(bandPx, 72, 168),
  };
};

/**
 * Column order:
 * Select · ID · Date · Who · Description · Supplier · Category · Orig Ccy · VAT rate ·
 * Gross Total · Net · (VAT page) VAT @ bands… · Total VAT · Actions
 */
const vatTxColgroup = (bandCount: number, fit: VatTxFitWidths, actionsWidthPx: number) => {
  const cols: Array<{ key: string; width?: string }> = [
    { key: 'select', width: `${VAT_TX_COMPACT.checkbox}px` },
    { key: 'id', width: `${fit.id}px` },
    { key: 'date', width: `${fit.date}px` },
    { key: 'who', width: `${VAT_TX_COMPACT.who}px` },
    { key: 'description', width: '20%' },
    { key: 'supplier' },
    { key: 'category', width: '13%' },
    { key: 'ccy', width: `${fit.ccy}px` },
    { key: 'rate', width: `${fit.rate}px` },
    { key: 'total', width: `${fit.total}px` },
    { key: 'net', width: `${fit.net}px` },
  ];
  for (let i = 0; i < bandCount; i += 1) {
    cols.push({ key: `band-${i}`, width: `${fit.band}px` });
  }
  cols.push({ key: 'vat', width: `${fit.vat}px` });
  cols.push({ key: 'actions', width: `${actionsWidthPx}px` });
  return cols;
};

/** Parse Revolut JSON text/object blobs used on ledger rows (`card_data`, etc.). */
const parseVatTxJsonObject = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* ignore */
    }
  }
  return {};
};

/** Cardholder display name from nested `transaction.card_data` / `card` (same as Tx Review Who). */
const cardholderDisplayNameFromVatRow = (row: VatRow | null | undefined): string => {
  const tx = row?.transaction;
  if (!tx || typeof tx !== 'object') return '';
  const card = {
    ...parseVatTxJsonObject(tx.card_data),
    ...parseVatTxJsonObject(tx.card),
  };
  const parts = [card.first_name, card.last_name]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  return String(tx.cardholder || tx.card_holder || '').trim();
};

const vatWhoInitialsForRow = (row: VatRow): WhoInitials =>
  whoInitialsFromCardholder(cardholderDisplayNameFromVatRow(row));

const vatWhoTooltipForRow = (row: VatRow): string => {
  const who = vatWhoInitialsForRow(row);
  return whoTooltipLabel(cardholderDisplayNameFromVatRow(row), who);
};

type VatTxSortDir = 'asc' | 'desc';
type VatTxSortKey =
  | 'id'
  | 'date'
  | 'who'
  | 'description'
  | 'vat_rate'
  | 'supplier'
  | 'category'
  | 'ccy'
  | 'total'
  | 'net'
  | 'vat'
  | `band:${string}`;

const VAT_TX_NUMERIC_SORT_KEYS = new Set<string>(['id', 'date', 'total', 'net', 'vat']);

const compareVatTxSortValues = (a: VatRow, b: VatRow, key: VatTxSortKey): number => {
  const strCmp = (left: string, right: string) =>
    left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true });
  if (key.startsWith('band:')) {
    const band = key.slice('band:'.length);
    const av = rowVatAmountForBand(a, band);
    const bv = rowVatAmountForBand(b, band);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return av - bv;
  }
  switch (key) {
    case 'id':
      return Number(a.id || 0) - Number(b.id || 0);
    case 'date':
      return (rowDate(a)?.getTime() ?? 0) - (rowDate(b)?.getTime() ?? 0);
    case 'who': {
      const whoCmp = strCmp(vatWhoInitialsForRow(a), vatWhoInitialsForRow(b));
      if (whoCmp !== 0) return whoCmp;
      return strCmp(cardholderDisplayNameFromVatRow(a), cardholderDisplayNameFromVatRow(b));
    }
    case 'description':
      return strCmp(String(a.description || '').trim(), String(b.description || '').trim());
    case 'vat_rate':
      return strCmp(String(a.vat_rate || ''), String(b.vat_rate || ''));
    case 'supplier':
      return strCmp(formatSupplier(a), formatSupplier(b));
    case 'category':
      return strCmp(formatCategory(a), formatCategory(b));
    case 'ccy':
      return strCmp(originalCurrencyForRow(a) || '', originalCurrencyForRow(b) || '');
    case 'total':
      return rowAmountBreakdown(a).total - rowAmountBreakdown(b).total;
    case 'net':
      return rowAmountBreakdown(a).net - rowAmountBreakdown(b).net;
    case 'vat':
      return rowAmountBreakdown(a).vat - rowAmountBreakdown(b).vat;
    default:
      return 0;
  }
};

const sortVatTxRows = (rows: VatRow[], key: VatTxSortKey, dir: VatTxSortDir): VatRow[] => {
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const cmp = compareVatTxSortValues(a, b, key);
    if (cmp !== 0) return cmp * mul;
    // Secondary: newest date first (unless date is already the primary key).
    if (key !== 'date') {
      const byDate = (rowDate(b)?.getTime() ?? 0) - (rowDate(a)?.getTime() ?? 0);
      if (byDate !== 0) return byDate;
    }
    return Number(b.id || 0) - Number(a.id || 0);
  });
};

interface ResolvedDocument {
  document_uid: string;
  document_type: 'receipt' | 'sales_invoice' | 'purchase_invoice';
  file_name?: string | null;
  open_url: string;
  link_id?: number | null;
}

interface ResolvedDocumentsByType {
  receipt: ResolvedDocument | null;
  sales_invoice: ResolvedDocument | null;
  purchase_invoice: ResolvedDocument | null;
}

interface AnyDocument {
  uid: string;
  document_type: 'receipt' | 'sales_invoice' | 'purchase_invoice';
  file_name?: string | null;
  created_at?: string | null;
}

const VAT: React.FC<{ variant?: TaxReturnPageVariant }> = ({ variant = 'vat' }) => {
  const isAnnual = variant === 'annual';
  const idPrefix = isAnnual ? 'annual' : 'vat';
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialFilters] = useState(() =>
    parseVatFiltersFromSearch(new URLSearchParams(window.location.search), variant)
  );
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [rowsTruncated, setRowsTruncated] = useState(false);
  const [rows, setRows] = useState<VatRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<VatRow | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [docsCatalogLoaded, setDocsCatalogLoaded] = useState(false);
  const [committedFilter, setCommittedFilter] = useState<CommittedFilter>(initialFilters.committed);
  const [tagsFilter, setTagsFilter] = useState<TagsFilter>(initialFilters.tags);
  const [regionFilter, setRegionFilter] = useState<RegionFilter>(initialFilters.region);
  const [revolutStatusFilter, setRevolutStatusFilter] = useState<RevolutStatusFilter>(
    initialFilters.revolutStatus
  );
  const [originalCurrencyFilter, setOriginalCurrencyFilter] = useState<OriginalCurrencyFilter>(
    initialFilters.originalCurrency
  );
  const [hideZeroVatRows, setHideZeroVatRows] = useState<boolean>(initialFilters.hideZeroVat);
  const [vatClaimedFilter, setVatClaimedFilter] = useState<VatClaimedFilter>(initialFilters.vatClaimed);
  const [cashflowFilter, setCashflowFilter] = useState<CashflowSideFilter>(
    initialFilters.side
  );
  const [cashSideFilter, setCashSideFilter] = useState<CashflowSideFilter>(
    initialFilters.cashSide
  );
  const [chartSupplierFilter, setChartSupplierFilter] = useState<ChartSupplierFilter>(
    initialFilters.chartSupplier && initialFilters.chartSection
      ? { sectionKey: initialFilters.chartSection, supplier: initialFilters.chartSupplier }
      : null
  );
  const [chartCategoryFilter, setChartCategoryFilter] = useState<ChartCategoryFilter>(null);
  const [selectedTxIds, setSelectedTxIds] = useState<Set<number>>(() => new Set());
  const [vatClaimBusy, setVatClaimBusy] = useState(false);
  const [invoiceFetchBusy, setInvoiceFetchBusy] = useState(false);
  const [invoiceFetchStatus, setInvoiceFetchStatus] = useState('');
  const [supplierPortalReadyByName, setSupplierPortalReadyByName] = useState<Record<string, boolean>>(
    {}
  );
  const [supplierPortalSessionHealthByName, setSupplierPortalSessionHealthByName] = useState<
    Record<string, { status: string; message: string }>
  >({});
  const [selectedYear, setSelectedYear] = useState<number>(initialFilters.year);
  const [selectedBimonth, setSelectedBimonth] = useState<BimonthPeriod>(initialFilters.period);
  const [txSortKey, setTxSortKey] = useState<VatTxSortKey>('date');
  const [txSortDir, setTxSortDir] = useState<VatTxSortDir>('desc');
  const [pageAccordionOpen, setPageAccordionOpen] = useState<Record<string, boolean>>({
    [VAT_SUMMARY_SECTION_KEY]: true,
  });
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docTitle, setDocTitle] = useState<string>('');
  const [resolvedDocsByTxId, setResolvedDocsByTxId] = useState<Record<number, ResolvedDocumentsByType>>({});
  const [allDocuments, setAllDocuments] = useState<AnyDocument[]>([]);
  const [selectedDocByType, setSelectedDocByType] = useState<Record<'receipt' | 'sales_invoice' | 'purchase_invoice', string>>({
    receipt: '',
    sales_invoice: '',
    purchase_invoice: '',
  });
  const [linking, setLinking] = useState(false);
  const [unlinkingLinkId, setUnlinkingLinkId] = useState<number | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState<'receipt' | 'sales_invoice' | 'purchase_invoice'>('receipt');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadInvoiceNumber, setUploadInvoiceNumber] = useState('');
  const [uploadInvoiceDate, setUploadInvoiceDate] = useState('');
  const [uploadSupplierName, setUploadSupplierName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [headingCategories, setHeadingCategories] = useState<VatCategoryOption[]>([]);
  const [granularCategories, setGranularCategories] = useState<VatCategoryOption[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [vatRateOptions, setVatRateOptions] = useState<string[]>(VAT_RATE_FALLBACK_OPTIONS);
  const [propOptions, setPropOptions] = useState<VatPropOption[]>([]);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagNotice, setTagNotice] = useState('');
  const [commentInternalDraft, setCommentInternalDraft] = useState('');
  const [commentExternalDraft, setCommentExternalDraft] = useState('');
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [commentsNotice, setCommentsNotice] = useState('');
  const [tagDraft, setTagDraft] = useState<{
    cashflowId: string;
    categoryId: string;
    supplier: string;
    vatRate: string;
    propIds: number[];
  }>({
    cashflowId: '',
    categoryId: '',
    supplier: '',
    vatRate: '',
    propIds: [],
  });

  useEffect(() => {
    startTransition(() => {
      setSearchParams((prev) => {
        const next = writeVatFiltersToSearch(prev, {
          year: selectedYear,
          period: selectedBimonth,
          region: regionFilter,
          committed: committedFilter,
          tags: tagsFilter,
          revolutStatus: revolutStatusFilter,
          originalCurrency: originalCurrencyFilter,
          hideZeroVat: hideZeroVatRows,
          vatClaimed: vatClaimedFilter,
          side: cashflowFilter,
          cashSide: cashSideFilter,
          chartSupplier: chartSupplierFilter?.supplier || '',
          chartSection: chartSupplierFilter?.sectionKey || '',
        }, variant);
        return next.toString() === prev.toString() ? prev : next;
      }, { replace: true });
    });
  }, [
    selectedYear,
    selectedBimonth,
    regionFilter,
    committedFilter,
    tagsFilter,
    revolutStatusFilter,
    originalCurrencyFilter,
    hideZeroVatRows,
    vatClaimedFilter,
    cashflowFilter,
    cashSideFilter,
    chartSupplierFilter,
    variant,
    setSearchParams,
  ]);

  useEffect(() => {
    const fromUrl = parseVatFiltersFromSearch(searchParams, variant);
    if (fromUrl.year !== selectedYear) setSelectedYear(fromUrl.year);
    if (!isAnnual && fromUrl.period !== selectedBimonth) setSelectedBimonth(fromUrl.period);
    if (fromUrl.region !== regionFilter) setRegionFilter(fromUrl.region);
    if (fromUrl.committed !== committedFilter) setCommittedFilter(fromUrl.committed);
    if (fromUrl.tags !== tagsFilter) setTagsFilter(fromUrl.tags);
    if (fromUrl.revolutStatus !== revolutStatusFilter) setRevolutStatusFilter(fromUrl.revolutStatus);
    if (fromUrl.originalCurrency !== originalCurrencyFilter) {
      setOriginalCurrencyFilter(fromUrl.originalCurrency);
    }
    if (fromUrl.hideZeroVat !== hideZeroVatRows) setHideZeroVatRows(fromUrl.hideZeroVat);
    if (fromUrl.vatClaimed !== vatClaimedFilter) setVatClaimedFilter(fromUrl.vatClaimed);
    if (fromUrl.side !== cashflowFilter) setCashflowFilter(fromUrl.side);
    if (fromUrl.cashSide !== cashSideFilter) setCashSideFilter(fromUrl.cashSide);
    const nextChartFilter: ChartSupplierFilter =
      fromUrl.chartSupplier && fromUrl.chartSection
        ? { sectionKey: fromUrl.chartSection, supplier: fromUrl.chartSupplier }
        : null;
    const sameChartFilter =
      (chartSupplierFilter?.sectionKey || '') === (nextChartFilter?.sectionKey || '') &&
      (chartSupplierFilter?.supplier || '') === (nextChartFilter?.supplier || '');
    if (!sameChartFilter) setChartSupplierFilter(nextChartFilter);
    // Only react to URL changes (back/forward / shared links). State→URL sync is the other effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchResolvedDocuments = async (
    inputRows: VatRow[]
  ): Promise<Record<number, ResolvedDocumentsByType>> => {
    const txIds = inputRows
      .map((r) => Number(r.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (txIds.length === 0) {
      return {};
    }

    const chunkSize = 200;
    const batches: number[][] = [];
    for (let i = 0; i < txIds.length; i += chunkSize) {
      batches.push(txIds.slice(i, i + chunkSize));
    }
    const settled = await Promise.all(
      batches.map((batch) =>
        api.get('/api/transaction-documents/resolved', {
          params: { transaction_ids: batch.join(',') },
        })
      )
    );
    const nextMap: Record<number, ResolvedDocumentsByType> = {};
    settled.forEach((res) => {
      const resolvedRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      resolvedRows.forEach((row: any) => {
        const txId = Number(row?.transaction_id);
        if (!Number.isFinite(txId)) return;
        nextMap[txId] = {
          receipt: row?.documents?.receipt ?? null,
          sales_invoice: row?.documents?.sales_invoice ?? null,
          purchase_invoice: row?.documents?.purchase_invoice ?? null,
        };
      });
    });
    return nextMap;
  };

  const fetchAllDocuments = async (force = false) => {
    if (docsCatalogLoaded && !force) return;
    const res = await api.get('/api/documents');
    const docs = Array.isArray(res.data) ? res.data : [];
    setAllDocuments(docs);
    setDocsCatalogLoaded(true);
  };

  useEffect(() => {
    let cancelled = false;
    const loadTagOptions = async () => {
      try {
        const [catRes, supplierRes, vatRes, propRes] = await Promise.all([
          api.get('/api/categories/', { params: { is_active: 'true' } }).catch(() => ({ data: { categories: [] } })),
          api.get('/api/suppliers/', { params: { is_active: 'true' } }).catch(() => ({ data: { suppliers: [] } })),
          api
            .get('/api/transaction-classification-rules/vat-rate-options')
            .catch(() => ({ data: { vat_rate_options: [] } })),
          api.get('/api/properties/').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const cats: VatCategoryOption[] = (Array.isArray(catRes.data?.categories) ? catRes.data.categories : [])
          .map((c: { id?: unknown; name?: unknown; pl_class?: unknown }) => ({
            id: Number(c.id),
            name: String(c.name || '').trim(),
            pl_class: String(c.pl_class || 'unset'),
          }))
          .filter((c: VatCategoryOption) => Number.isFinite(c.id) && c.id > 0 && c.name);
        setHeadingCategories(
          cats
            .filter((c) => isTopLevelCashflowHeadingName(c.name))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setGranularCategories(
          cats
            .filter((c) => !isTopLevelCashflowHeadingName(c.name))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        const supplierRows = Array.isArray(supplierRes.data?.suppliers)
          ? supplierRes.data.suppliers
          : [];
        const suppliers = supplierRows
          .map((s: { name?: unknown }) => String(s?.name || '').trim())
          .filter(Boolean)
          .sort((a: string, b: string) => a.localeCompare(b));
        setSupplierOptions(Array.from(new Set(suppliers)) as string[]);
        const readyMap: Record<string, boolean> = {};
        const healthMap: Record<string, { status: string; message: string }> = {};
        supplierRows.forEach(
          (s: {
            name?: unknown;
            invoice_url?: unknown;
            invoice_portal_username?: unknown;
            has_invoice_portal_password?: unknown;
            invoice_fetch_ui_steps?: unknown;
            invoice_fetch_plugin_key?: unknown;
            has_invoice_portal_storage_state?: unknown;
            invoice_portal_session_health?: {
              status?: unknown;
              message?: unknown;
            };
          }) => {
            const name = String(s?.name || '').trim().toLowerCase();
            if (!name) return;
            const steps = s?.invoice_fetch_ui_steps;
            const hasSteps =
              steps != null &&
              typeof steps === 'object' &&
              !Array.isArray(steps) &&
              Object.keys(steps as object).length > 0;
            const loginUrl =
              typeof steps === 'object' &&
              steps &&
              !Array.isArray(steps) &&
              (steps as { login?: { url?: string } }).login?.url;
            const hasUrl = Boolean(String(s?.invoice_url || loginUrl || '').trim());
            const pluginKey = String(s?.invoice_fetch_plugin_key || '').trim();
            const sessionStatus = String(s?.invoice_portal_session_health?.status || '').trim();
            const sessionMessage = String(s?.invoice_portal_session_health?.message || '').trim();
            if (pluginKey && sessionStatus) {
              healthMap[name] = { status: sessionStatus, message: sessionMessage };
            }
            const sessionBlocks =
              sessionStatus === 'expired' ||
              sessionStatus === 'invalid' ||
              sessionStatus === 'missing';
            const passwordPluginReady = Boolean(
              pluginKey === 'three_ie' &&
                hasUrl &&
                String(s?.invoice_portal_username || '').trim() &&
                s?.has_invoice_portal_password
            );
            const pluginReady = Boolean(
              pluginKey &&
                hasUrl &&
                ((s?.has_invoice_portal_storage_state && !sessionBlocks) || passwordPluginReady)
            );
            const configReady = Boolean(
              hasUrl &&
                String(s?.invoice_portal_username || '').trim() &&
                s?.has_invoice_portal_password &&
                hasSteps
            );
            readyMap[name] = pluginReady || configReady;
          }
        );
        setSupplierPortalReadyByName(readyMap);
        setSupplierPortalSessionHealthByName(healthMap);
        const vatOpts = (Array.isArray(vatRes.data?.vat_rate_options) ? vatRes.data.vat_rate_options : [])
          .map((x: { value?: unknown } | string) =>
            canonicalizeVatRate(typeof x === 'string' ? x : String(x?.value || ''))
          )
          .filter(Boolean);
        setVatRateOptions(vatOpts.length ? Array.from(new Set(vatOpts)) : VAT_RATE_FALLBACK_OPTIONS);
        const propList = Array.isArray(propRes.data)
          ? propRes.data
          : Array.isArray(propRes.data?.properties)
            ? propRes.data.properties
            : [];
        const nickRank = new Map<string, number>(TX_PROP_NICKNAMES.map((n, i) => [n.toUpperCase(), i]));
        setPropOptions(
          (propList as Array<{ id?: unknown; nickname?: unknown }>)
            .map((p) => ({
              id: Number(p.id),
              nickname: String(p.nickname || '').trim(),
            }))
            .filter(
              (p) =>
                Number.isFinite(p.id) &&
                p.id > 0 &&
                nickRank.has(p.nickname.toUpperCase())
            )
            .sort(
              (a, b) =>
                (nickRank.get(a.nickname.toUpperCase()) ?? 99) -
                (nickRank.get(b.nickname.toUpperCase()) ?? 99)
            )
        );
      } catch {
        if (!cancelled) {
          setVatRateOptions(VAT_RATE_FALLBACK_OPTIONS);
        }
      }
    };
    void loadTagOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchRows = async () => {
      try {
        setLoading(true);
        setError('');
        setRowsTruncated(false);
        const { dateFrom, dateTo } = isAnnual
          ? yearDateWindow(selectedYear)
          : bimonthDateWindow(selectedBimonth, selectedYear);
        const { rows: nextRows, truncated } = await fetchVatCompactReviewRows({
          committed: committedFilter,
          dateFrom,
          dateTo,
          includeDeclined: revolutStatusFilter === 'all',
        });
        if (cancelled) return;
        setRowsTruncated(truncated);
        setRows(nextRows);
        setResolvedDocsByTxId({});
        setSelectedRow((prev) => {
          if (prev && nextRows.some((r) => r.id === prev.id)) {
            return nextRows.find((r) => r.id === prev.id) || prev;
          }
          return null;
        });
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error
            || (isAnnual ? 'Failed to load annual return transactions' : 'Failed to load VAT transactions')
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRows();
    return () => {
      cancelled = true;
    };
  }, [committedFilter, revolutStatusFilter, selectedYear, selectedBimonth, isAnnual]);

  useEffect(() => {
    setCommentsNotice('');
  }, [selectedRow?.id]);

  useEffect(() => {
    if (!selectedRow) {
      setCommentInternalDraft('');
      setCommentExternalDraft('');
      return;
    }
    setCommentInternalDraft(String(selectedRow.comment_internal || ''));
    setCommentExternalDraft(String(selectedRow.comment_external || ''));
  }, [selectedRow?.id, selectedRow?.comment_internal, selectedRow?.comment_external]);

  useEffect(() => {
    if (!selectedRow) {
      setTagDraft({
        cashflowId: '',
        categoryId: '',
        supplier: '',
        vatRate: '',
        propIds: [],
      });
      return;
    }
    const cashName = String(selectedRow.cashFlow || '').trim().toLowerCase();
    const cashMatch =
      headingCategories.find((c) => c.name.trim().toLowerCase() === cashName)
      || headingCategories.find((c) => {
        if (cashName === 'in' || cashName === 'income') {
          return c.name.trim().toLowerCase() === 'in' || c.name.trim().toLowerCase() === 'income';
        }
        if (cashName === 'out' || cashName === 'expense') {
          return c.name.trim().toLowerCase() === 'out' || c.name.trim().toLowerCase() === 'expense';
        }
        if (cashName === 'n/a' || cashName === 'na') {
          return c.name.trim().toLowerCase() === 'n/a' || c.name.trim().toLowerCase() === 'na';
        }
        return false;
      });
    const catName = String(selectedRow.category || '').trim().toLowerCase();
    const catMatch = granularCategories.find((c) => c.name.trim().toLowerCase() === catName);
    const supplier = String(selectedRow.supplier || '').trim();
    const vatRate = canonicalizeVatRate(selectedRow.vat_rate);
    setTagDraft({
      cashflowId: cashMatch ? String(cashMatch.id) : '',
      categoryId: catMatch ? String(catMatch.id) : '',
      supplier: supplier === '—' || supplier === '-' ? '' : supplier,
      vatRate,
      propIds: rowPropertyIds(selectedRow),
    });
  }, [
    selectedRow,
    headingCategories,
    granularCategories,
  ]);

  const yearOptions = useMemo(() => {
    const now = currentYear();
    const years = new Set<number>([selectedYear, now]);
    for (let y = now; y >= now - 6; y -= 1) years.add(y);
    years.add(now + 1);
    rows.forEach((row) => {
      const d = rowDate(row);
      if (d) years.add(d.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [rows, selectedYear]);

  const originalCurrencyOptions = useMemo(() => {
    const wanted = regionFilter === 'ire' ? 'IRE' : 'UK';
    const codes = new Set<string>();
    if (originalCurrencyFilter !== 'all') codes.add(originalCurrencyFilter);
    rows.forEach((row) => {
      if (regionForRow(row) !== wanted) return;
      const code = originalCurrencyForRow(row);
      if (code) codes.add(code);
    });
    if (codes.size === 0) codes.add(DEFAULT_ORIGINAL_CURRENCY);
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [rows, originalCurrencyFilter, regionFilter]);

  /** Filters shared by both region tabs (everything except IE/UK split). */
  const filteredRowsBeforeRegion = useMemo(() => {
    let filtered = rows;
    if (revolutStatusFilter === 'completed') {
      filtered = filtered.filter(isRevolutCompletedOrBlank);
    }
    if (originalCurrencyFilter !== 'all') {
      filtered = filtered.filter(
        (row) => originalCurrencyForRow(row) === originalCurrencyFilter
      );
    }
    if (tagsFilter !== 'all') {
      filtered = filtered.filter((row) => rowMatchesTagsFilter(row, tagsFilter));
    }
    if (!isAnnual && hideZeroVatRows) {
      filtered = filtered.filter((row) => !isZeroPercentVatRateRow(row));
    }
    if (!isAnnual && vatClaimedFilter === 'claimed') {
      filtered = filtered.filter((row) => isVatClaimed(row));
    } else if (!isAnnual && vatClaimedFilter === 'unclaimed') {
      filtered = filtered.filter((row) => !isVatClaimed(row));
    }
    filtered = filtered.filter((row) => {
      const d = rowDate(row);
      if (!d) return false;
      if (d.getFullYear() !== selectedYear) return false;
      if (isAnnual) return true;
      return bimonthForMonth(d.getMonth()) === selectedBimonth;
    });
    return filtered;
  }, [
    rows,
    revolutStatusFilter,
    originalCurrencyFilter,
    tagsFilter,
    hideZeroVatRows,
    vatClaimedFilter,
    selectedYear,
    selectedBimonth,
    isAnnual,
  ]);

  const regionTabCounts = useMemo(
    () => ({
      ire: filteredRowsBeforeRegion.filter((row) => regionForRow(row) === 'IRE').length,
      uk: filteredRowsBeforeRegion.filter((row) => regionForRow(row) === 'UK').length,
    }),
    [filteredRowsBeforeRegion]
  );

  const plClassByName = useMemo(
    () => buildPlClassByName(granularCategories),
    [granularCategories]
  );

  const rowAnnualBucket = (row: VatRow): AnnualPlBucket | null =>
    annualPlBucket(cashflowBucket(row), plClassFromReviewRow(row, plClassByName), {
      description: row.description,
    });

  const annualPlByRegion = useMemo(
    () => ({
      ire: buildAnnualPlChartsFromRows(
        filteredRowsBeforeRegion.filter(
          (row) =>
            regionForRow(row) === 'IRE' &&
            (rowAnnualBucket(row) === 'income' || rowAnnualBucket(row) === 'expenses')
        )
      ),
      uk: buildAnnualPlChartsFromRows(
        filteredRowsBeforeRegion.filter(
          (row) =>
            regionForRow(row) === 'UK' &&
            (rowAnnualBucket(row) === 'income' || rowAnnualBucket(row) === 'expenses')
        )
      ),
    }),
    [filteredRowsBeforeRegion, plClassByName]
  );

  const annualCashByRegion = useMemo(
    () => ({
      ire: buildAnnualCashChartsFromRows(
        filteredRowsBeforeRegion.filter((row) => regionForRow(row) === 'IRE')
      ),
      uk: buildAnnualCashChartsFromRows(
        filteredRowsBeforeRegion.filter((row) => regionForRow(row) === 'UK')
      ),
    }),
    [filteredRowsBeforeRegion]
  );

  const activeRegionLabel = regionFilter === 'ire' ? 'IE' : 'UK';
  const annualDisplayCurrency = isAnnual && regionFilter === 'uk' ? 'GBP' : 'EUR';
  const activePlCharts = annualPlByRegion[regionFilter];
  const activeCashCharts = annualCashByRegion[regionFilter];
  const activeCombinedCharts = useMemo(
    () =>
      combineAnnualPlAndCashCharts(activePlCharts, activeCashCharts, {
        foldIntoCurrency: annualDisplayCurrency,
      }),
    [activePlCharts, activeCashCharts, annualDisplayCurrency]
  );

  const annualSummaryPlCounts = useMemo(() => {
    if (!isAnnual) return { income: 0, expense: 0 };
    const wanted = regionFilter === 'ire' ? 'IRE' : 'UK';
    let income = 0;
    let expense = 0;
    filteredRowsBeforeRegion.forEach((row) => {
      if (regionForRow(row) !== wanted) return;
      const bucket = rowAnnualBucket(row);
      if (bucket === 'income') income += 1;
      else if (bucket === 'expenses') expense += 1;
    });
    return { income, expense };
  }, [filteredRowsBeforeRegion, regionFilter, isAnnual, plClassByName]);

  const sortedRows = useMemo(() => {
    const wanted = regionFilter === 'ire' ? 'IRE' : 'UK';
    return filteredRowsBeforeRegion
      .filter((row) => regionForRow(row) === wanted)
      .sort((a, b) => {
        const da = rowDate(a)?.getTime() ?? 0;
        const db = rowDate(b)?.getTime() ?? 0;
        if (db !== da) return db - da;
        return Number(b.id || 0) - Number(a.id || 0);
      });
  }, [filteredRowsBeforeRegion, regionFilter]);

  const selectedPeriodRangeLabel = useMemo(
    () => (isAnnual ? String(selectedYear) : bimonthRangeLabel(selectedBimonth, selectedYear)),
    [isAnnual, selectedBimonth, selectedYear]
  );

  const cashflowSections = useMemo(() => {
    if (!isAnnual) {
      const cashflowIn: VatRow[] = [];
      const cashflowOut: VatRow[] = [];
      for (const row of sortedRows) {
        const bucket = cashflowBucket(row);
        if (bucket === 'in') cashflowIn.push(row);
        else if (bucket === 'out') cashflowOut.push(row);
      }
      return [
        { key: 'in', title: 'Cashflow In', rows: cashflowIn },
        { key: 'out', title: 'Cashflow Out', rows: cashflowOut },
      ];
    }

    const grouped: Record<AnnualPlBucket, VatRow[]> = {
      income: [],
      expenses: [],
      internal_in: [],
      internal_out: [],
      financing_in: [],
      financing_out: [],
      capital_in: [],
      capital_out: [],
      tax_in: [],
      tax_out: [],
      reversal_in: [],
      reversal_out: [],
      needs: [],
    };
    for (const row of sortedRows) {
      const flow = cashflowBucket(row);
      if (cashSideFilter === 'in' && flow !== 'in') continue;
      if (cashSideFilter === 'out' && flow !== 'out') continue;
      const bucket = rowAnnualBucket(row);
      if (!bucket) continue;
      grouped[bucket].push(row);
    }
    return ANNUAL_DISPLAY_SECTIONS.filter((section) => {
      if (cashflowFilter === 'in') return section.key === 'income';
      if (cashflowFilter === 'out') return section.key === 'expenses';
      return true;
    }).map((section) => ({
      key: section.key,
      title: section.title,
      rows: rowsForAnnualDisplaySection(grouped, section),
    }));
  }, [sortedRows, isAnnual, cashflowFilter, cashSideFilter, plClassByName]);

  const cashflowCategorySummaries = useMemo(
    () =>
      cashflowSections.map((section) => ({
        key: section.key,
        title: section.title,
        rows: section.rows,
        summaries: buildCategoryGrossSummaries(section.rows),
      })),
    [cashflowSections]
  );

  useEffect(() => {
    if (!selectedRow) {
      if (detailPanelOpen) setDetailPanelOpen(false);
      return;
    }
    if (sortedRows.some((r) => r.id === selectedRow.id)) return;
    setSelectedRow(null);
    setDetailPanelOpen(false);
  }, [sortedRows, selectedRow, detailPanelOpen]);

  const visibleTxIdsKey = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => id > 0).join(','),
    [sortedRows]
  );

  // Resolve receipt/invoice links for the visible (period + region) rows only; do not block table paint.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    const run = async () => {
      try {
        const nextMap = await fetchResolvedDocuments(sortedRows);
        if (!cancelled) setResolvedDocsByTxId(nextMap);
      } catch {
        if (!cancelled) setResolvedDocsByTxId({});
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on visible row ids + loading, not the full row objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, visibleTxIdsKey]);

  const cashflowVatSummaries = useMemo(
    () =>
      cashflowSections.map((section) => ({
        key: section.key,
        title: section.title,
        rows: section.rows,
        summaries: isAnnual ? [] : buildRegionVatSummaries(section.rows),
      })),
    [cashflowSections, isAnnual]
  );

  const familyTransfersView = useMemo(() => {
    if (!isAnnual) {
      return buildFamilyTransfersView<VatRow>([]);
    }
    return buildFamilyTransfersView(sortedRows, {
      amountScaleForRow: (row) =>
        scaleToCurrency(String(row.currency || 'EUR'), annualDisplayCurrency),
      plClassForRow: (row) => plClassFromReviewRow(row, plClassByName),
    });
  }, [isAnnual, sortedRows, annualDisplayCurrency, plClassByName]);

  const familyTransferChartRows = useMemo(
    () => buildFamilyTransferChartRows(familyTransfersView),
    [familyTransfersView]
  );

  const pageAccordionKeys = useMemo(
    () => [
      VAT_SUMMARY_SECTION_KEY,
      ...(isAnnual
        ? [
            FAMILY_TRANSFERS_SECTION_KEY,
            ...familyTransfersView.families.map((family) =>
              familyTransferAccordionKey(family.familyKey)
            ),
          ]
        : []),
      ...cashflowVatSummaries.map((section) => section.key),
    ],
    [cashflowVatSummaries, isAnnual, familyTransfersView.families]
  );

  const isPageAccordionOpen = (key: string, fallback: boolean) =>
    pageAccordionOpen[key] ?? fallback;

  /** Any expanded section → Close all (first click). All closed → Open all. */
  const anyPageAccordionOpen = useMemo(
    () =>
      pageAccordionKeys.some((key) => {
        if (key === VAT_SUMMARY_SECTION_KEY) {
          return isPageAccordionOpen(key, true);
        }
        if (key === FAMILY_TRANSFERS_SECTION_KEY) {
          return isPageAccordionOpen(key, familyTransfersView.rows.length > 0);
        }
        if (key.startsWith('family_transfers:')) {
          const familyKey = key.slice('family_transfers:'.length);
          const family = familyTransfersView.families.find((row) => row.familyKey === familyKey);
          return isPageAccordionOpen(key, (family?.rows.length ?? 0) > 0);
        }
        const section = cashflowVatSummaries.find((row) => row.key === key);
        return isPageAccordionOpen(key, (section?.rows.length ?? 0) > 0);
      }),
    [pageAccordionKeys, pageAccordionOpen, cashflowVatSummaries, familyTransfersView]
  );

  const setPageAccordionExpanded = useCallback((key: string, expanded: boolean) => {
    setPageAccordionOpen((prev) => (prev[key] === expanded ? prev : { ...prev, [key]: expanded }));
  }, []);

  const setAllPageAccordions = useCallback(
    (open: boolean) => {
      startTransition(() => {
        const next: Record<string, boolean> = {};
        pageAccordionKeys.forEach((key) => {
          next[key] = open;
        });
        setPageAccordionOpen(next);
      });
    },
    [pageAccordionKeys]
  );

  const scrollToVatDetail = (keys: string[]) => {
    const unique = Array.from(new Set(keys.filter(Boolean)));
    if (unique.length === 0) return;
    const targetId = vatDetailSectionId(unique[0]);
    setPageAccordionOpen((prev) => {
      const next = { ...prev };
      unique.forEach((key) => {
        next[key] = true;
      });
      return next;
    });
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const latest = document.getElementById(targetId);
          if (!latest) return;
          scrollSectionBelowStickyBar({
            target: latest,
            scrollParent: document.getElementById(LAYOUT_MAIN_SCROLL_ID),
            stickyBar: document.querySelector<HTMLElement>(
              `[data-testid="${TAX_RETURN_FILTER_BAR_TEST_ID}"]`
            ),
          });
        });
      });
    }, 0);
  };

  /** Per cashflow section: stacked Net+VAT bars with one column per supplier (by currency). */
  const cashflowSupplierCharts = useMemo(() => {
    type SupplierAmounts = { supplier: string; gross: number; net: number; vat: number; count: number };
    type CurrencyChart = {
      currency: string;
      suppliers: Array<{
        supplier: string;
        Gross: number;
        Net: number;
        VAT: number;
        labelTotal: number;
        count: number;
      }>;
      gbpConverted?: boolean;
      gbpToEurRate?: number;
    };

    const round = (n: number) => Number(n.toFixed(2));

    return cashflowSections.map((section) => {
      const byCurrency = new Map<string, Map<string, SupplierAmounts>>();

      section.rows.forEach((row) => {
        const currency = String(row.currency || 'EUR').trim().toUpperCase() || 'EUR';
        const supplier = String(row.supplier || '').trim() || '—';
        const grossAbs = Math.abs(Number(row.amount || 0));
        if (!Number.isFinite(grossAbs) || grossAbs <= 0) return;
        const { ratePct } = normalizeVatBand(row.vat_rate);
        const { net, vat } = splitGrossVat(grossAbs, ratePct);

        let suppliers = byCurrency.get(currency);
        if (!suppliers) {
          suppliers = new Map();
          byCurrency.set(currency, suppliers);
        }
        const existing = suppliers.get(supplier);
        if (existing) {
          existing.gross += grossAbs;
          existing.net += net;
          existing.vat += vat;
          existing.count += 1;
        } else {
          suppliers.set(supplier, {
            supplier,
            gross: grossAbs,
            net,
            vat,
            count: 1,
          });
        }
      });

      const charts: CurrencyChart[] = Array.from(byCurrency.entries())
        .map(([currency, suppliers]) => ({
          currency,
          suppliers: Array.from(suppliers.values())
            .sort((a, b) => b.gross - a.gross || a.supplier.localeCompare(b.supplier))
            .map((s) => ({
              supplier: s.supplier,
              Gross: round(s.gross),
              Net: round(s.net),
              VAT: round(s.vat),
              labelTotal: round(s.gross),
              count: s.count,
            })),
        }))
        .sort((a, b) => a.currency.localeCompare(b.currency));

      return {
        key: section.key,
        charts: isAnnual ? foldGbpIntoEurSupplierCharts(charts) : charts,
      };
    });
  }, [cashflowSections, isAnnual]);

  const annualSectionSignedCharts = useMemo(() => {
    if (!isAnnual) {
      return [] as Array<{
        key: string;
        inGross: number;
        outGross: number;
        inCount: number;
        outCount: number;
        categories: ReturnType<typeof buildSignedInOutCategoryRows>;
        suppliers: ReturnType<typeof buildSignedInOutSupplierRows>;
        categoryChartRows: Array<{
          label: string;
          In: number;
          Out: number;
          inCount: number;
          outCount: number;
        }>;
        supplierChartRows: Array<{
          label: string;
          In: number;
          Out: number;
          inCount: number;
          outCount: number;
        }>;
      }>;
    }
    const supplierLines = (rows: VatRow[]) => {
      const byName = new Map<string, { supplier: string; gross: number; count: number }>();
      rows.forEach((row) => {
        const supplier = String(row.supplier || '').trim() || '—';
        const scale = scaleToCurrency(row.currency, annualDisplayCurrency);
        const gross = Math.abs(Number(row.amount || 0)) * scale;
        if (!Number.isFinite(gross) || gross <= 0) return;
        const existing = byName.get(supplier);
        if (existing) {
          existing.gross += gross;
          existing.count += 1;
          return;
        }
        byName.set(supplier, { supplier, gross, count: 1 });
      });
      return Array.from(byName.values());
    };
    return cashflowSections.map((section) => {
      const inRows = section.rows.filter((row) => cashflowBucket(row) === 'in');
      const outRows = section.rows.filter((row) => cashflowBucket(row) === 'out');
      const inFolded = foldCategorySummariesIntoCurrency(
        buildCategoryGrossSummaries(inRows),
        annualDisplayCurrency
      );
      const outFolded = foldCategorySummariesIntoCurrency(
        buildCategoryGrossSummaries(outRows),
        annualDisplayCurrency
      );
      const categories = buildSignedInOutCategoryRows(inFolded[0]?.lines || [], outFolded[0]?.lines || []);
      const suppliers = buildSignedInOutSupplierRows(supplierLines(inRows), supplierLines(outRows));
      return {
        key: section.key,
        inGross: Number(inFolded[0]?.gross || 0),
        outGross: Number(outFolded[0]?.gross || 0),
        inCount: inRows.length,
        outCount: outRows.length,
        categories,
        suppliers,
        categoryChartRows: categories.map((row) => ({
          label: row.category,
          In: row.In,
          Out: row.Out,
          inCount: row.inCount,
          outCount: row.outCount,
        })),
        supplierChartRows: suppliers.map((row) => ({
          label: row.supplier,
          In: row.In,
          Out: row.Out,
          inCount: row.inCount,
          outCount: row.outCount,
        })),
      };
    });
  }, [cashflowSections, isAnnual, annualDisplayCurrency]);

  const annualSectionSignedChartsByKey = useMemo(() => {
    const map = new Map<
      string,
      (typeof annualSectionSignedCharts)[number]
    >();
    annualSectionSignedCharts.forEach((row) => map.set(row.key, row));
    return map;
  }, [annualSectionSignedCharts]);

  /** Supplier chart scoped to active category filter (drill-down: category → suppliers). */
  const sectionSupplierChartRowsByKey = useMemo(() => {
    if (!isAnnual) return new Map<string, Array<{
      label: string;
      In: number;
      Out: number;
      inCount: number;
      outCount: number;
    }>>();
    const categoryForRow = (row: VatRow) =>
      annualSummaryChartCategory(formatCategory(row));
    const supplierLines = (rows: VatRow[]) => {
      const byName = new Map<string, { supplier: string; gross: number; count: number }>();
      rows.forEach((row) => {
        const supplier = String(row.supplier || '').trim() || '—';
        const scale = scaleToCurrency(row.currency, annualDisplayCurrency);
        const gross = Math.abs(Number(row.amount || 0)) * scale;
        if (!Number.isFinite(gross) || gross <= 0) return;
        const existing = byName.get(supplier);
        if (existing) {
          existing.gross += gross;
          existing.count += 1;
          return;
        }
        byName.set(supplier, { supplier, gross, count: 1 });
      });
      return Array.from(byName.values());
    };
    const map = new Map<
      string,
      Array<{
        label: string;
        In: number;
        Out: number;
        inCount: number;
        outCount: number;
      }>
    >();
    cashflowVatSummaries.forEach((section) => {
      let scopedRows = section.rows;
      if (chartCategoryFilter?.sectionKey === section.key) {
        scopedRows = filterRowsByChartCategory(
          section.rows,
          chartCategoryFilter,
          section.key,
          categoryForRow
        );
      }
      const inRows = scopedRows.filter((row) => cashflowBucket(row) === 'in');
      const outRows = scopedRows.filter((row) => cashflowBucket(row) === 'out');
      const suppliers = buildSignedInOutSupplierRows(
        supplierLines(inRows),
        supplierLines(outRows)
      );
      map.set(
        section.key,
        suppliers.map((row) => ({
          label: row.supplier,
          In: row.In,
          Out: row.Out,
          inCount: row.inCount,
          outCount: row.outCount,
        }))
      );
    });
    return map;
  }, [cashflowVatSummaries, chartCategoryFilter, isAnnual, annualDisplayCurrency]);

  const sectionFilteredRowsByKey = useMemo(() => {
    const categoryForRow = (row: VatRow) =>
      annualSummaryChartCategory(formatCategory(row));
    const map = new Map<string, VatRow[]>();
    cashflowVatSummaries.forEach((section) => {
      map.set(
        section.key,
        filterRowsByChartSupplierAndCategory(
          section.rows,
          chartSupplierFilter,
          chartCategoryFilter,
          section.key,
          categoryForRow
        )
      );
    });
    return map;
  }, [cashflowVatSummaries, chartSupplierFilter, chartCategoryFilter]);

  const sectionDisplayRowsByKey = useMemo(() => {
    const map = new Map<string, VatRow[]>();
    cashflowVatSummaries.forEach((section) => {
      const filtered = sectionFilteredRowsByKey.get(section.key) ?? section.rows;
      map.set(section.key, sortVatTxRows(filtered, txSortKey, txSortDir));
    });
    return map;
  }, [cashflowVatSummaries, sectionFilteredRowsByKey, txSortKey, txSortDir]);

  /** Summary title: Cashflow In totals minus Cashflow Out totals, by currency. */
  const summaryInMinusOut = useMemo(() => {
    type Acc = {
      currency: string;
      gross: number;
      net: number;
      vat: number;
      income: number;
      expenses: number;
    };
    const byCurrency = new Map<string, Acc>();
    const bump = (summaries: RegionVatSummary[], sign: 1 | -1) => {
      summaries.forEach((summary) => {
        const currency = String(summary.currency || 'EUR').toUpperCase();
        const acc = byCurrency.get(currency) || {
          currency,
          gross: 0,
          net: 0,
          vat: 0,
          income: 0,
          expenses: 0,
        };
        acc.gross += sign * summary.gross;
        acc.net += sign * summary.net;
        acc.vat += sign * summary.vat;
        if (sign === 1) acc.income += summary.gross;
        else acc.expenses += summary.gross;
        byCurrency.set(currency, acc);
      });
    };
    const inSection = cashflowVatSummaries.find((section) => section.key === 'in');
    const outSection = cashflowVatSummaries.find((section) => section.key === 'out');
    bump(inSection?.summaries || [], 1);
    bump(outSection?.summaries || [], -1);
    return Array.from(byCurrency.values()).sort((a, b) => a.currency.localeCompare(b.currency));
  }, [cashflowVatSummaries]);

  const vatRateColumns = isAnnual ? [] : REGION_VAT_RATE_COLUMNS[regionFilter];
  const tableColSpan = 13 + vatRateColumns.length;

  /**
   * Chart series for Summary (per currency):
   * Cashflow In/Out stack Net + VAT (= Gross; do not also stack Gross — that double-counted).
   * Third column is VAT In − Out. Bar labels show Gross (or VAT for the net-VAT column).
   */
  const summaryChartByCurrency = useMemo(() => {
    type StackAmounts = { gross: number; net: number; vat: number };
    type SideRow = {
      side: string;
      Gross: number;
      Net: number;
      VAT: number;
      /** Top-of-bar label: Gross for cashflow columns, VAT for VAT In−Out. */
      labelTotal: number;
    };
    type CurrencyChart = {
      currency: string;
      sideRows: SideRow[];
    };

    const emptyStack = (): StackAmounts => ({ gross: 0, net: 0, vat: 0 });
    const byCurrency = new Map<
      string,
      { currency: string; totals: { in: StackAmounts; out: StackAmounts } }
    >();

    const ensure = (currency: string) => {
      const ccy = currency.toUpperCase();
      let acc = byCurrency.get(ccy);
      if (!acc) {
        acc = {
          currency: ccy,
          totals: { in: emptyStack(), out: emptyStack() },
        };
        byCurrency.set(ccy, acc);
      }
      return acc;
    };

    cashflowVatSummaries.forEach((section) => {
      const side = section.key === 'in' ? 'in' : 'out';
      section.summaries.forEach((summary) => {
        const acc = ensure(summary.currency);
        acc.totals[side].gross += summary.gross;
        acc.totals[side].net += summary.net;
        acc.totals[side].vat += summary.vat;
      });
    });

    const round = (n: number) => Number(n.toFixed(2));
    const sideRow = (
      side: string,
      gross: number,
      net: number,
      vat: number,
      labelTotal: number
    ): SideRow => {
      const Gross = round(gross);
      const Net = round(net);
      const VAT = round(vat);
      return {
        side,
        Gross,
        Net,
        VAT,
        labelTotal: round(labelTotal),
      };
    };

    return Array.from(byCurrency.values())
      .map((acc): CurrencyChart => {
        if (isAnnual) {
          const profit = acc.totals.in.gross - acc.totals.out.gross;
          return {
            currency: acc.currency,
            sideRows: [
              sideRow('Income', acc.totals.in.gross, 0, 0, acc.totals.in.gross),
              sideRow('Expenses', acc.totals.out.gross, 0, 0, acc.totals.out.gross),
              sideRow('Profit/Loss', profit, 0, 0, profit),
            ],
          };
        }
        const vatNet = acc.totals.in.vat - acc.totals.out.vat;
        return {
          currency: acc.currency,
          sideRows: [
            sideRow(
              'Cashflow In',
              acc.totals.in.gross,
              acc.totals.in.net,
              acc.totals.in.vat,
              acc.totals.in.gross
            ),
            sideRow(
              'Cashflow Out',
              acc.totals.out.gross,
              acc.totals.out.net,
              acc.totals.out.vat,
              acc.totals.out.gross
            ),
            sideRow('VAT In − Out', 0, 0, vatNet, vatNet),
          ],
        };
      })
      .sort((a, b) => a.currency.localeCompare(b.currency));
  }, [cashflowVatSummaries, isAnnual]);

  const exportActiveTabToXlsx = async () => {
    const regionLabel = regionFilter === 'ire' ? 'IE' : 'UK';
    const periodLabel = isAnnual
      ? String(selectedYear)
      : BIMONTH_PERIODS.find((period) => period.value === selectedBimonth)?.label || 'Period';

    // Re-fetch comments at export time: in-memory rows may predate comment writes or
    // an older vat_compact payload that omitted comment_external.
    const commentByTxId = new Map<number, string>();
    setExporting(true);
    try {
      try {
        const { dateFrom, dateTo } = isAnnual
          ? yearDateWindow(selectedYear)
          : bimonthDateWindow(selectedBimonth, selectedYear);
        const { rows: freshRows } = await fetchVatCompactReviewRows({
          committed: committedFilter,
          dateFrom,
          dateTo,
          includeDeclined: revolutStatusFilter === 'all',
        });
        for (const r of freshRows) {
          const c = String(r.comment_external || '').trim();
          if (!c) continue;
          const id = Number(r.id);
          if (Number.isFinite(id) && id > 0) commentByTxId.set(id, c);
        }
        // Keep the on-screen table in sync so a second export without reload still works.
        if (commentByTxId.size > 0) {
          setRows((prev) =>
            prev.map((row) => {
              const id = Number(row.id);
              const fresh = commentByTxId.get(id);
              if (!fresh || String(row.comment_external || '').trim() === fresh) return row;
              return { ...row, comment_external: fresh };
            })
          );
        }
      } catch {
        // Fall back to whatever is already on the row objects.
      }

      const commentForRow = (row: VatRow): string => {
        const id = Number(row.id);
        if (Number.isFinite(id) && commentByTxId.has(id)) {
          return commentByTxId.get(id) || '';
        }
        return String(row.comment_external || '').trim();
      };

      const summaryRows: Array<Array<string | number>> = isAnnual
        ? [['Side', 'Currency', 'Category', 'TXs', 'Amount']]
        : [['Cashflow', 'Region', 'Currency', 'VAT band', 'TXs', 'Gross', 'Net', 'VAT']];
      if (isAnnual) {
      const cashByCcy = new Map<string, { in: number; out: number }>();
      sortedRows.forEach((row) => {
        const flow = cashflowBucket(row);
        if (flow !== 'in' && flow !== 'out') return;
        const ccy = String(row.currency || 'EUR').trim().toUpperCase() || 'EUR';
        const grossAbs = Math.abs(Number(row.amount || 0));
        if (!Number.isFinite(grossAbs)) return;
        const acc = cashByCcy.get(ccy) || { in: 0, out: 0 };
        if (flow === 'in') acc.in += grossAbs;
        else acc.out += grossAbs;
        cashByCcy.set(ccy, acc);
      });
      Array.from(cashByCcy.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([currency, totals]) => {
          summaryRows.push(['Cash in', currency, 'ALL', '', Number(totals.in.toFixed(2))]);
          summaryRows.push(['Cash out', currency, 'ALL', '', Number(totals.out.toFixed(2))]);
          summaryRows.push([
            'Net cash',
            currency,
            'ALL',
            '',
            Number((totals.in - totals.out).toFixed(2)),
          ]);
        });
      cashflowCategorySummaries.forEach((section) => {
        section.summaries.forEach((summary) => {
          if (section.key === 'income' || section.key === 'expenses') {
            summaryRows.push([
              section.title,
              summary.currency,
              'ALL',
              '',
              Number(summary.gross.toFixed(2)),
            ]);
          }
          summary.lines.forEach((line) => {
            summaryRows.push([
              section.title,
              summary.currency,
              line.category,
              line.count,
              Number(line.gross.toFixed(2)),
            ]);
          });
        });
      });
    } else {
    cashflowVatSummaries.forEach((section) => {
      const cashflowLabel = section.key === 'in' ? 'In' : 'Out';
      section.summaries.forEach((summary) => {
        summaryRows.push([
          cashflowLabel,
          summary.region,
          summary.currency,
          'ALL',
          summary.count,
          Number(summary.gross.toFixed(2)),
          Number(summary.net.toFixed(2)),
          Number(summary.vat.toFixed(2)),
        ]);
        summary.bands.forEach((band) => {
          summaryRows.push([
            cashflowLabel,
            summary.region,
            summary.currency,
            band.band,
            band.count,
            Number(band.gross.toFixed(2)),
            Number(band.net.toFixed(2)),
            Number(band.vat.toFixed(2)),
          ]);
        });
      });
    });
    }

    // Match on-screen TX table columns (plus Cashflow to distinguish In/Out); omit Actions / Who.
    // Comment (external) = accountant-facing note from tx_appended_data.comment_external
    // (e.g. paired payment-reversal TX ids).
    const detailHeader: Array<string | number> = isAnnual
      ? [
          'Section',
          'Side',
          'TX ID',
          'Date',
          'Description',
          'Supplier',
          'Category',
          'Ccy',
          'Amount',
          'Comment (external)',
        ]
      : [
          'Cashflow',
          'TX ID',
          'Date',
          'Description',
          'Supplier',
          'Category',
          'Ccy',
          'VAT rate',
          'Gross Total',
          'Net',
          ...vatRateColumns.map((band) => `VAT @ ${band}`),
          'Total VAT',
          'Comment (external)',
        ];
    const detailRows: Array<Array<string | number>> = [detailHeader];
    cashflowSections.forEach((section) => {
      section.rows.forEach((row) => {
        const amounts = rowAmountBreakdown(row);
        const commentExternal = commentForRow(row);
        const side =
          cashflowBucket(row) === 'out' ? 'Out' : cashflowBucket(row) === 'in' ? 'In' : '—';
        if (isAnnual) {
          detailRows.push([
            section.title,
            side,
            row.id,
            formatRowDate(row),
            String(row.description || ''),
            formatSupplier(row),
            formatCategory(row),
            originalCurrencyForRow(row) || '',
            Number(amounts.total.toFixed(2)),
            commentExternal,
          ]);
          return;
        }
        detailRows.push([
          section.key === 'in' ? 'In' : 'Out',
          row.id,
          formatRowDate(row),
          String(row.description || ''),
          formatSupplier(row),
          formatCategory(row),
          originalCurrencyForRow(row) || '',
          String(row.vat_rate || ''),
          Number(amounts.total.toFixed(2)),
          Number(amounts.net.toFixed(2)),
          ...vatRateColumns.map((band) => {
            const bandVat = rowVatAmountForBand(row, band);
            return bandVat == null ? '' : Number(bandVat.toFixed(2));
          }),
          Number(amounts.vat.toFixed(2)),
          commentExternal,
        ]);
      });
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Details');
    const safePeriod = periodLabel.replace(/[^A-Za-z0-9]+/g, '-');
    const filePrefix = isAnnual ? 'AnnualReturns' : 'VAT';
    XLSX.writeFile(
      workbook,
      isAnnual
        ? `${filePrefix}_${regionLabel}_${safePeriod}.xlsx`
        : `${filePrefix}_${regionLabel}_${selectedYear}_${safePeriod}.xlsx`
    );
    } finally {
      setExporting(false);
    }
  };

  const formatAmount = (amount?: number | null, currency?: string | null) => {
    const ccy = (currency || 'EUR').toUpperCase();
    const value = Number(amount || 0);
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatSignedAmount = (amount?: number | null, currency?: string | null) => {
    const ccy = (currency || 'EUR').toUpperCase();
    const value = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: ccy,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: 'exceptZero',
      }).format(value);
    } catch {
      return formatAmount(value, ccy);
    }
  };

  const formatTableAmount = (amount?: number | null, currency?: string | null) =>
    isAnnual ? formatSignedAmount(amount, currency) : formatAmount(amount, currency);

  const formatAmountWhole = (amount?: number | null, currency?: string | null) => {
    const ccy = (currency || 'EUR').toUpperCase();
    const value = Math.round(Number(amount || 0));
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatChartTickWhole = (v: number) => {
    const n = Math.round(v);
    return Math.abs(n) >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
  };

  const applyAnnualPlColumnFilter = (
    region: RegionFilter,
    currency: string,
    sideLabel: string
  ) => {
    const nextSide: CashflowSideFilter =
      sideLabel === 'Income' ? 'in' : sideLabel === 'Expenses' ? 'out' : 'all';
    const ccy = String(currency || '').trim().toUpperCase();
    const skipCurrencyPin = !ccy || ccy === 'ALL';
    const alreadySelected =
      nextSide !== 'all' &&
      regionFilter === region &&
      cashflowFilter === nextSide &&
      cashSideFilter === 'all' &&
      (skipCurrencyPin
        ? originalCurrencyFilter === 'all'
        : originalCurrencyFilter === 'all' || originalCurrencyFilter === ccy);
    setRegionFilter(region);
    if (skipCurrencyPin) setOriginalCurrencyFilter('all');
    else setOriginalCurrencyFilter(ccy as OriginalCurrencyFilter);
    setCashSideFilter('all');
    setCashflowFilter(alreadySelected ? 'all' : nextSide);
  };

  const applyAnnualCashColumnFilter = (
    region: RegionFilter,
    currency: string,
    sideLabel: string
  ) => {
    const nextSide: CashflowSideFilter =
      sideLabel === 'Cash in' ? 'in' : sideLabel === 'Cash out' ? 'out' : 'all';
    const ccy = String(currency || '').trim().toUpperCase();
    const skipCurrencyPin = !ccy || ccy === 'ALL';
    const alreadySelected =
      nextSide !== 'all' &&
      regionFilter === region &&
      cashSideFilter === nextSide &&
      cashflowFilter === 'all' &&
      (skipCurrencyPin
        ? originalCurrencyFilter === 'all'
        : originalCurrencyFilter === 'all' || originalCurrencyFilter === ccy);
    setRegionFilter(region);
    if (skipCurrencyPin) setOriginalCurrencyFilter('all');
    else setOriginalCurrencyFilter(ccy as OriginalCurrencyFilter);
    setCashflowFilter('all');
    setCashSideFilter(alreadySelected ? 'all' : nextSide);
  };

  const applyChartSupplierColumnFilter = useCallback(
    (sectionKey: string, supplier: string) => {
      startTransition(() => {
        setChartSupplierFilter((prev) => toggleChartSupplierFilter(prev, sectionKey, supplier));
        setPageAccordionExpanded(sectionKey, true);
      });
    },
    [setPageAccordionExpanded]
  );

  const applyChartCategoryColumnFilter = useCallback(
    (sectionKey: string, category: string) => {
      startTransition(() => {
        setChartCategoryFilter((prev) => {
          const next = toggleChartCategoryFilter(prev, sectionKey, category);
          if (prev?.sectionKey === sectionKey && next?.category !== prev.category) {
            setChartSupplierFilter((sPrev) =>
              sPrev?.sectionKey === sectionKey ? null : sPrev
            );
          }
          return next;
        });
        setPageAccordionExpanded(sectionKey, true);
      });
    },
    [setPageAccordionExpanded]
  );

  const clearSectionChartCategoryFilter = useCallback((sectionKey: string) => {
    startTransition(() => {
      setChartCategoryFilter((prev) => (prev?.sectionKey === sectionKey ? null : prev));
      setChartSupplierFilter((prev) => (prev?.sectionKey === sectionKey ? null : prev));
    });
  }, []);

  const chartCategoryForRow = (row: VatRow): string =>
    annualSummaryChartCategory(formatCategory(row));

  const renderAnnualInOutHeadingSegment = (
    side: 'in' | 'out',
    amount: number,
    count: number
  ): React.ReactNode => {
    const isIn = side === 'in';
    return (
      <>
        {' · '}
        <Box
          component="span"
          sx={{ fontWeight: 700, color: isIn ? '#16A34A' : '#DC2626' }}
        >
          {isIn ? 'In' : 'Out'}:
        </Box>{' '}
        {formatAmountWhole(amount, annualDisplayCurrency)} ({count} Tx)
      </>
    );
  };

  const renderAnnualSectionHeadingTotals = (sectionKey: string) => {
    const signed = annualSectionSignedChartsByKey.get(sectionKey);
    const inGross = signed?.inGross ?? 0;
    const outGross = signed?.outGross ?? 0;
    const inCount = signed?.inCount ?? 0;
    const outCount = signed?.outCount ?? 0;
    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {renderAnnualInOutHeadingSegment('in', inGross, inCount)}
        {renderAnnualInOutHeadingSegment('out', outGross, outCount)}
      </Typography>
    );
  };

  const renderAnnualSummaryHeadingTotals = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        · {activeRegionLabel} {regionTabCounts[regionFilter]} TX
      </Typography>
      {activeCombinedCharts.map((chart) => {
        const incomePl = chart.sideRows.find((row) => row.side === 'In')?.pl ?? 0;
        const expensePl = chart.sideRows.find((row) => row.side === 'Out')?.pl ?? 0;
        const profitPl = chart.sideRows.find((row) => row.side === 'Net')?.pl ?? 0;
        return (
          <Typography
            key={`summary-pl-title-${regionFilter}-${chart.currency}`}
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.2 }}
          >
            · Income {formatAmountWhole(incomePl, chart.currency)} ({annualSummaryPlCounts.income} Tx)
            {' · Expenses '}
            {formatAmountWhole(expensePl, chart.currency)} ({annualSummaryPlCounts.expense} Tx)
            {' · '}
            {profitPl >= 0 ? 'Profit' : 'Loss'} {formatAmountWhole(Math.abs(profitPl), chart.currency)}
          </Typography>
        );
      })}
    </>
  );

  const renderVatSectionHeadingTotals = (
    section: (typeof cashflowVatSummaries)[number]
  ) => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {section.rows.length} TX
      </Typography>
      {section.summaries.map((summary) => (
        <Typography
          key={`${section.key}-tx-title-${summary.region}-${summary.currency}`}
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.2 }}
        >
          · Gross {formatAmountWhole(summary.gross, summary.currency)}
          {' · '}
          Net {formatAmountWhole(summary.net, summary.currency)}
          {' · '}
          VAT {formatAmountWhole(summary.vat, summary.currency)}
        </Typography>
      ))}
    </>
  );

  const supplierColumnOpacity = (sectionKey: string, supplier: string): number => {
    if (!chartSupplierFilter || chartSupplierFilter.sectionKey !== sectionKey) return 1;
    return chartSupplierFilter.supplier === supplier ? 1 : 0.32;
  };

  const renderAnnualCombinedChart = (
    chart: ReturnType<typeof combineAnnualPlAndCashCharts>[number],
    regionLabel: string,
    regionKey: RegionFilter
  ) => {
    const displayRows = annualCombinedChartDisplayRows(chart.sideRows);
    const moneyTip = (value: number | string, name: string) => [
      typeof value === 'number' ? formatAmountWhole(value, chart.currency) : value,
      name,
    ];
    const chartCcy = String(chart.currency || '').toUpperCase();
    const filterCcy = chart.gbpConverted ? 'all' : chartCcy;
    const dimCurrency =
      originalCurrencyFilter !== 'all' &&
      originalCurrencyFilter !== chartCcy &&
      !(chart.gbpConverted && originalCurrencyFilter === 'GBP');
    const clickSeries = (series: 'pl' | 'cash', side: string) => {
      if (series === 'pl') {
        applyAnnualPlColumnFilter(
          regionKey,
          filterCcy,
          side === 'In' ? 'Income' : side === 'Out' ? 'Expenses' : 'Profit/Loss'
        );
      } else {
        applyAnnualCashColumnFilter(
          regionKey,
          filterCcy,
          side === 'In' ? 'Cash in' : side === 'Out' ? 'Cash out' : 'Net cash'
        );
      }
    };
    const opacityFor = (series: 'pl' | 'cash', side: string) => {
      if (dimCurrency) return 0.32;
      const activeSide = series === 'pl' ? cashflowFilter : cashSideFilter;
      const otherActive = series === 'pl' ? cashSideFilter !== 'all' : cashflowFilter !== 'all';
      const dimSide =
        activeSide !== 'all' &&
        ((activeSide === 'in' && side !== 'In') || (activeSide === 'out' && side !== 'Out'));
      return dimSide || otherActive ? 0.32 : 1;
    };
    return (
      <Box key={`combined-${regionLabel}-${chart.currency}`}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {regionLabel} · P&L vs Cash ({chart.currency}
          {chart.gbpConverted
            ? `, GBP × ${chart.gbpToEurRate ?? DEFAULT_GBP_TO_EUR_RATE}`
            : ''}
          )
        </Typography>
        <Box sx={{ width: '100%', height: 320, cursor: 'pointer' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={displayRows}
              margin={{ top: 36, right: 8, left: 0, bottom: 8 }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="side" tick={{ fontSize: 12 }} />
              <YAxis width={52} tickFormatter={formatChartTickWhole} />
              <RechartsTooltip formatter={moneyTip} />
              <Legend wrapperStyle={{ pointerEvents: 'none' }} />
              <ReferenceLine y={0} stroke="#94A3B8" />
              <Bar
                dataKey="pl"
                name="P&L"
                fill="#2563EB"
                isAnimationActive={false}
                cursor="pointer"
                onClick={(item) => clickSeries('pl', String(item?.payload?.side || ''))}
              >
                {displayRows.map((row) => (
                  <Cell key={`pl-${row.side}`} fill="#2563EB" fillOpacity={opacityFor('pl', row.side)} />
                ))}
                <LabelList
                  dataKey="pl"
                  content={(props) => (
                    <OutsideColumnValueLabel
                      value={props.value}
                      viewBox={props.viewBox as { x?: number; y?: number; width?: number; height?: number }}
                      dense={false}
                      format={(n) => formatAmountWhole(n, chart.currency)}
                    />
                  )}
                />
              </Bar>
              <Bar
                dataKey="cash"
                name="Cash"
                fill="#F59E0B"
                isAnimationActive={false}
                cursor="pointer"
                onClick={(item) => clickSeries('cash', String(item?.payload?.side || ''))}
              >
                {displayRows.map((row) => (
                  <Cell key={`cash-${row.side}`} fill="#F59E0B" fillOpacity={opacityFor('cash', row.side)} />
                ))}
                <LabelList
                  dataKey="cash"
                  content={(props) => (
                    <OutsideColumnValueLabel
                      value={props.value}
                      viewBox={props.viewBox as { x?: number; y?: number; width?: number; height?: number }}
                      dense={false}
                      format={(n) => formatAmountWhole(n, chart.currency)}
                    />
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  /** Compact cols are content-fitted px (Classification Review); Description/Supplier/Category share leftover. */
  const vatTxFitWidths = useMemo(
    () => computeVatTxFitWidths(sortedRows, vatRateColumns),
    [sortedRows, vatRateColumns]
  );

  const vatTxColSx = useMemo(() => {
    const amountSx = {
      ...fitContentColSx(vatTxFitWidths.total),
      textAlign: 'right' as const,
      fontVariantNumeric: 'tabular-nums' as const,
    };
    const amountHeaderSx = { ...amountSx, textAlign: 'center' as const };
    const netSx = {
      ...fitContentColSx(vatTxFitWidths.net),
      textAlign: 'right' as const,
      fontVariantNumeric: 'tabular-nums' as const,
    };
    const vatSx = {
      ...fitContentColSx(vatTxFitWidths.vat),
      textAlign: 'right' as const,
      fontVariantNumeric: 'tabular-nums' as const,
      fontWeight: 700,
    };
    const bandSx = {
      ...fitContentColSx(vatTxFitWidths.band),
      textAlign: 'right' as const,
      fontVariantNumeric: 'tabular-nums' as const,
    };
    return {
      select: {
        ...compactColSx(VAT_TX_COMPACT.checkbox),
        textAlign: 'center' as const,
        overflow: 'visible' as const,
        px: '0 !important',
      },
      id: {
        ...fitContentColSx(vatTxFitWidths.id),
        fontVariantNumeric: 'tabular-nums' as const,
      },
      date: {
        ...fitContentColSx(vatTxFitWidths.date),
        fontVariantNumeric: 'tabular-nums' as const,
      },
      who: {
        ...fitContentColSx(VAT_TX_COMPACT.who),
        textAlign: 'center' as const,
      },
      description: FIT_FLEX_CELL_SX,
      supplier: FIT_FLEX_CELL_SX,
      category: FIT_FLEX_CELL_SX,
      ccy: {
        ...fitContentColSx(vatTxFitWidths.ccy),
        textAlign: 'center' as const,
      },
      rate: {
        ...fitContentColSx(vatTxFitWidths.rate),
        textAlign: 'center' as const,
      },
      total: { ...amountSx, fontWeight: 700 },
      net: netSx,
      vat: { ...vatSx, pr: '12px' },
      band: (_band: string) => bandSx,
      actions: {
        ...fitContentColSx(
          isAnnual ? VAT_TX_COMPACT.actionsAnnual : VAT_TX_COMPACT.actionsVat
        ),
        textAlign: 'right' as const,
        overflow: 'hidden' as const,
        pl: '4px',
      },
      amountHeader: amountHeaderSx,
      netHeader: { ...netSx, textAlign: 'center' as const },
      vatHeader: { ...vatSx, textAlign: 'center' as const, fontWeight: 700 },
      bandHeader: (_band: string) => ({ ...bandSx, textAlign: 'center' as const }),
    };
  }, [vatTxFitWidths, isAnnual]);

  /** Open URL comes only from linked transaction_documents (resolved open_url). */
  const documentUrlFor = (row: VatRow, kind: DocKind): string | null => {
    const resolved = resolvedDocsByTxId[row.id];
    const resolvedDoc = kind === 'receipt'
      ? resolved?.receipt
      : kind === 'salesInvoice'
        ? resolved?.sales_invoice
        : resolved?.purchase_invoice;
    const u = resolvedDoc?.open_url;
    if (!u) return null;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    const base = String(api.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}${u}`;
  };

  const openDoc = (row: VatRow, kind: DocKind) => {
    const url = documentUrlFor(row, kind);
    if (!url) return;
    setDocUrl(url);
    setDocTitle(
      kind === 'receipt'
        ? `Receipt for TX ${row.id}`
        : kind === 'salesInvoice'
          ? `Sales Invoice for TX ${row.id}`
          : `Purchase Doc for TX ${row.id}`
    );
  };

  const openRowDetail = (row: VatRow) => {
    setSelectedRow(row);
    setDetailPanelOpen(true);
    setTagNotice('');
    void fetchAllDocuments();
  };

  const closeRowDetail = () => {
    setDetailPanelOpen(false);
  };

  const patchLocalRow = (txId: number, patch: Partial<VatRow>) => {
    setRows((prev) => prev.map((r) => (r.id === txId ? { ...r, ...patch } : r)));
    setSelectedRow((prev) => (prev && prev.id === txId ? { ...prev, ...patch } : prev));
  };

  const saveAppendedComments = async () => {
    if (!selectedRow?.id) return;
    const txId = selectedRow.id;
    setCommentsSaving(true);
    setCommentsNotice('');
    setError('');
    try {
      const res = await api.patch(`/api/revolut/transactions/complete/${txId}/appended-comments`, {
        comment_internal: commentInternalDraft,
        comment_external: commentExternalDraft,
      });
      const nextInternal = String(res.data?.comment_internal ?? commentInternalDraft ?? '');
      const nextExternal = String(res.data?.comment_external ?? commentExternalDraft ?? '');
      patchLocalRow(txId, {
        comment_internal: nextInternal,
        comment_external: nextExternal,
      });
      setCommentInternalDraft(nextInternal);
      setCommentExternalDraft(nextExternal);
      setCommentsNotice('Comments saved');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save comments');
    } finally {
      setCommentsSaving(false);
    }
  };

  const applyVatClaimLocal = (txIds: number[], claimed: boolean) => {
    const status = claimed ? 'claimed' : 'unclaimed';
    const idSet = new Set(txIds);
    setRows((prev) =>
      prev.map((r) =>
        idSet.has(r.id)
          ? {
              ...r,
              vat_claim_status: status,
              flags: { ...(r.flags || {}), vat_claimed: claimed },
            }
          : r
      )
    );
    setSelectedRow((prev) =>
      prev && idSet.has(prev.id)
        ? {
            ...prev,
            vat_claim_status: status,
            flags: { ...(prev.flags || {}), vat_claimed: claimed },
          }
        : prev
    );
  };

  const toggleVatClaimed = async (row: VatRow) => {
    const next = !isVatClaimed(row);
    applyVatClaimLocal([row.id], next);
    try {
      setError('');
      const res = await api.patch(`/api/revolut/transactions/complete/${row.id}/appended-vat-claim`, {
        vat_claimed: next,
      });
      const claimed =
        typeof res.data?.vat_claimed === 'boolean' ? Boolean(res.data.vat_claimed) : next;
      applyVatClaimLocal([row.id], claimed);
    } catch (err: any) {
      applyVatClaimLocal([row.id], !next);
      setError(err?.response?.data?.error || 'Failed to update VAT claim status');
    }
  };

  const bulkSetVatClaimed = async (claimed: boolean) => {
    const ids = Array.from(selectedTxIds);
    if (!ids.length) return;
    applyVatClaimLocal(ids, claimed);
    try {
      setVatClaimBusy(true);
      setError('');
      await api.patch('/api/revolut/transactions/complete/appended-vat-claim-bulk', {
        transaction_ids: ids,
        vat_claimed: claimed,
      });
      setSelectedTxIds(new Set());
    } catch (err: any) {
      applyVatClaimLocal(ids, !claimed);
      setError(err?.response?.data?.error || 'Failed to bulk-update VAT claim status');
    } finally {
      setVatClaimBusy(false);
    }
  };

  const supplierInvoiceFetchReady = (row: VatRow): boolean => {
    const name = formatSupplier(row).trim().toLowerCase();
    if (!name || name === '—') return false;
    return Boolean(supplierPortalReadyByName[name]);
  };

  const supplierInvoiceSessionHealth = (
    row: VatRow
  ): { status: string; message: string } | null => {
    const name = formatSupplier(row).trim().toLowerCase();
    if (!name || name === '—') return null;
    return supplierPortalSessionHealthByName[name] || null;
  };

  const supplierInvoiceFetchTooltip = (row: VatRow): string => {
    const health = supplierInvoiceSessionHealth(row);
    if (health?.status === 'expired' || health?.status === 'invalid' || health?.status === 'missing') {
      return health.message || 'Supplier portal session expired — re-export storage_state';
    }
    if (health?.status === 'expiring_soon') {
      return `${health.message} Fetch may still work.`;
    }
    if (supplierInvoiceFetchReady(row)) {
      return 'Fetch latest invoice from supplier portal';
    }
    return 'Supplier portal fetch not configured (URL, username, password, UI steps, or plugin session)';
  };

  const selectedPortalSessionWarnings = (() => {
    const msgs: string[] = [];
    const seen = new Set<string>();
    selectedTxIds.forEach((id) => {
      const row = sortedRows.find((r) => r.id === id);
      if (!row || cashflowBucket(row) !== 'out') return;
      const health = supplierInvoiceSessionHealth(row);
      if (!health) return;
      if (
        health.status !== 'expired' &&
        health.status !== 'expiring_soon' &&
        health.status !== 'invalid' &&
        health.status !== 'missing'
      ) {
        return;
      }
      const name = formatSupplier(row).trim() || 'Supplier';
      const key = `${name}:${health.status}`;
      if (seen.has(key)) return;
      seen.add(key);
      msgs.push(`${name}: ${health.message || health.status}`);
    });
    return msgs;
  })();

  const pollInvoiceFetchBatch = async (batchId: string) => {
    const terminal = new Set(['succeeded', 'failed', 'skipped']);
    for (let i = 0; i < 120; i += 1) {
      const res = await api.get('/api/supplier-invoice-fetch/jobs', {
        params: { batch_id: batchId },
      });
      const jobs = Array.isArray(res.data?.jobs) ? res.data.jobs : [];
      const complete = Boolean(res.data?.complete) || (
        jobs.length > 0 && jobs.every((j: { status?: string }) => terminal.has(String(j.status || '')))
      );
      const counts = res.data?.counts || {};
      setInvoiceFetchStatus(
        `Fetch: ${counts.succeeded || 0} ok, ${counts.failed || 0} failed, ${counts.skipped || 0} skipped, ${
          (counts.queued || 0) + (counts.running || 0)
        } pending`
      );
      if (complete) {
        const succeededIds = jobs
          .filter((j: { status?: string; transaction_id?: number }) => j.status === 'succeeded')
          .map((j: { transaction_id?: number }) => Number(j.transaction_id))
          .filter((id: number) => id > 0);
        if (succeededIds.length) {
          const map = await fetchResolvedDocuments(
            succeededIds.map((id: number) => ({ id } as VatRow))
          );
          setResolvedDocsByTxId((prev) => ({ ...prev, ...map }));
        }
        return res.data;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    throw new Error('Invoice fetch timed out while waiting for jobs to finish');
  };

  const enqueueInvoiceFetch = async (txIds: number[]) => {
    const ids = Array.from(new Set(txIds.filter((id) => Number.isFinite(id) && id > 0)));
    if (!ids.length) return;
    try {
      setInvoiceFetchBusy(true);
      setError('');
      setInvoiceFetchStatus('Enqueueing invoice fetch…');
      const res = await api.post('/api/supplier-invoice-fetch/jobs', {
        transaction_ids: ids,
      });
      const batchId = String(res.data?.batch_id || '');
      if (!batchId) {
        throw new Error('No batch_id returned');
      }
      await pollInvoiceFetchBatch(batchId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to fetch supplier invoices');
      setInvoiceFetchStatus('');
    } finally {
      setInvoiceFetchBusy(false);
    }
  };

  const bulkFetchSupplierInvoices = async () => {
    const outIds = Array.from(selectedTxIds).filter((id) => {
      const row = sortedRows.find((r) => r.id === id);
      return row && cashflowBucket(row) === 'out';
    });
    await enqueueInvoiceFetch(outIds);
  };

  const toggleTxSelected = (txId: number, checked: boolean) => {
    setSelectedTxIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(txId);
      else next.delete(txId);
      return next;
    });
  };

  const toggleSectionSelectAll = (sectionRows: VatRow[], checked: boolean) => {
    setSelectedTxIds((prev) => {
      const next = new Set(prev);
      sectionRows.forEach((r) => {
        if (checked) next.add(r.id);
        else next.delete(r.id);
      });
      return next;
    });
  };

  const granularCategoryIdsOnRow = (row: VatRow): number[] => {
    const fromTags = (Array.isArray(row.tags) ? row.tags : [])
      .filter((t) => !isTopLevelCashflowHeadingName(tagCategoryName(t)))
      .map((t) => {
        const id = tagCategoryId(t);
        if (id != null) return id;
        const name = tagCategoryName(t).toLowerCase();
        return granularCategories.find((c) => c.name.trim().toLowerCase() === name)?.id ?? 0;
      })
      .filter((id) => id > 0);
    if (fromTags.length) return Array.from(new Set(fromTags));
    const byName = granularCategories.find(
      (c) => c.name.trim().toLowerCase() === String(row.category || '').trim().toLowerCase()
    )?.id;
    return byName && byName > 0 ? [byName] : [];
  };

  const stampSupplierVatOnCategories = async (
    txId: number,
    categoryIds: number[],
    opts: { supplier?: string; vatRate?: string }
  ) => {
    const supplier = String(opts.supplier || '').trim() || undefined;
    const vatRate = canonicalizeVatRate(opts.vatRate) || undefined;
    if (!supplier && !vatRate) return;
    if (supplier) {
      for (const categoryId of categoryIds) {
        await postManualRevolutCategoryTagIgnoreDuplicate({
          revolut_transaction_id: txId,
          category_id: categoryId,
          source: 'manual',
          supplier_name: supplier,
        });
      }
    }
    if (vatRate) {
      await setManualTxVatRate(txId, vatRate);
    }
  };

  const saveCashflowTag = async (categoryIdRaw: string) => {
    if (!selectedRow) return;
    const categoryId = Number(categoryIdRaw);
    if (!Number.isFinite(categoryId) || categoryId <= 0) return;
    const heading = headingCategories.find((c) => c.id === categoryId);
    if (!heading) return;
    setTagBusy(true);
    setTagNotice('');
    setError('');
    try {
      setTagDraft((prev) => ({ ...prev, cashflowId: String(categoryId) }));
      await postManualRevolutCategoryTagIgnoreDuplicate({
        revolut_transaction_id: selectedRow.id,
        category_id: categoryId,
        source: 'manual',
      });
      patchLocalRow(selectedRow.id, { cashFlow: heading.name });
      setTagNotice(`Cash flow set to ${heading.name}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update cash flow');
    } finally {
      setTagBusy(false);
    }
  };

  const saveCategoryTag = async (categoryIdRaw: string) => {
    if (!selectedRow) return;
    const categoryId = Number(categoryIdRaw);
    if (!Number.isFinite(categoryId) || categoryId <= 0) return;
    const category = granularCategories.find((c) => c.id === categoryId);
    if (!category) return;
    setTagBusy(true);
    setTagNotice('');
    setError('');
    try {
      setTagDraft((prev) => ({ ...prev, categoryId: String(categoryId) }));
      const vatRate = canonicalizeVatRate(tagDraft.vatRate) || undefined;
      const supplier = String(tagDraft.supplier || '').trim() || undefined;
      await postManualRevolutCategoryTagIgnoreDuplicate({
        revolut_transaction_id: selectedRow.id,
        category_id: categoryId,
        source: 'manual',
        ...(supplier ? { supplier_name: supplier } : {}),
      });
      if (vatRate) {
        await setManualTxVatRate(selectedRow.id, vatRate);
      }
      patchLocalRow(selectedRow.id, {
        category: category.name,
        ...(vatRate ? { vat_rate: vatRate } : {}),
        ...(supplier ? { supplier } : {}),
      });
      setTagNotice(`Category set to ${category.name}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update category');
    } finally {
      setTagBusy(false);
    }
  };

  const saveSupplierTag = async (supplierName: string) => {
    if (!selectedRow) return;
    const supplier = String(supplierName || '').trim();
    setTagDraft((prev) => ({ ...prev, supplier }));
    const categoryIds = [
      ...granularCategoryIdsOnRow(selectedRow),
      ...(tagDraft.categoryId ? [Number(tagDraft.categoryId)] : []),
    ].filter((id) => Number.isFinite(id) && id > 0);
    const uniqueCategoryIds = Array.from(new Set(categoryIds));
    if (!supplier) return;
    if (!uniqueCategoryIds.length) {
      setError('Pick a Category first, then set Supplier.');
      return;
    }
    setTagBusy(true);
    setTagNotice('');
    setError('');
    try {
      await stampSupplierVatOnCategories(selectedRow.id, uniqueCategoryIds, {
        supplier,
        vatRate: tagDraft.vatRate,
      });
      patchLocalRow(selectedRow.id, { supplier });
      setTagNotice(`Supplier set to ${supplier}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update supplier');
    } finally {
      setTagBusy(false);
    }
  };

  const saveVatRateTag = async (vatRateRaw: string) => {
    if (!selectedRow) return;
    const vatRate = canonicalizeVatRate(vatRateRaw);
    setTagDraft((prev) => ({ ...prev, vatRate }));
    if (!vatRate) return;
    setTagBusy(true);
    setTagNotice('');
    setError('');
    try {
      await setManualTxVatRate(selectedRow.id, vatRate);
      patchLocalRow(selectedRow.id, { vat_rate: vatRate });
      setTagNotice(`VAT rate set to ${vatRate}.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update VAT rate');
    } finally {
      setTagBusy(false);
    }
  };

  const savePropTags = async (propIdsRaw: unknown) => {
    if (!selectedRow) return;
    const nextIds = (Array.isArray(propIdsRaw) ? propIdsRaw : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    setTagDraft((prev) => ({ ...prev, propIds: nextIds }));
    setTagBusy(true);
    setTagNotice('');
    setError('');
    try {
      const res = await api.patch(`/api/revolut/transactions/complete/${selectedRow.id}/appended-property`, {
        property_ids: nextIds,
      });
      const fromApi = Array.isArray(res.data?.properties)
        ? res.data.properties
            .map((p: { id?: number; nickname?: string | null }) => ({
              id: Number(p?.id),
              nickname:
                typeof p?.nickname === 'string' && p.nickname.trim()
                  ? p.nickname.trim()
                  : null,
            }))
            .filter((p: { id: number }) => Number.isFinite(p.id) && p.id > 0)
        : nextIds.map((id) => {
            const opt = propOptions.find((o) => o.id === id);
            return { id, nickname: opt?.nickname || null };
          });
      const nicknames = fromApi
        .map((p: { nickname: string | null }) => p.nickname)
        .filter((n: string | null): n is string => Boolean(n));
      patchLocalRow(selectedRow.id, {
        property_ids: fromApi.map((p: { id: number }) => p.id),
        properties: fromApi,
        property_nicknames: nicknames,
        property_nickname: nicknames[0] || null,
        property_id: fromApi[0]?.id ?? null,
      });
      setTagNotice(nicknames.length ? `Prop set to ${nicknames.join(', ')}.` : 'Prop cleared.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update property');
    } finally {
      setTagBusy(false);
    }
  };

  const docCell = (row: VatRow, kind: DocKind, label: string) => {
    const url = documentUrlFor(row, kind);
    if (!url) return <Typography variant="body2" color="text.secondary">-</Typography>;
    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={(event) => {
          event.stopPropagation();
          openDoc(row, kind);
        }}
      >
        {label}
      </Link>
    );
  };

  const selectedTxDocs = selectedRow ? resolvedDocsByTxId[selectedRow.id] : null;
  const selectedAmounts = selectedRow ? rowAmountBreakdown(selectedRow) : null;

  const handleTxSort = (key: VatTxSortKey) => {
    if (txSortKey === key) {
      setTxSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setTxSortKey(key);
    setTxSortDir(
      VAT_TX_NUMERIC_SORT_KEYS.has(key) || key.startsWith('band:') ? 'desc' : 'asc'
    );
  };

  const renderVatTxTable = (sectionRows: VatRow[], rowsPresorted = false) => {
    const sectionTotals = buildVatTxTableTotals(sectionRows, vatRateColumns);
    const displayRows = rowsPresorted
      ? sectionRows
      : sortVatTxRows(sectionRows, txSortKey, txSortDir);
    const allSectionSelected =
      displayRows.length > 0 && displayRows.every((r) => selectedTxIds.has(r.id));
    const someSectionSelected = displayRows.some((r) => selectedTxIds.has(r.id));
    const truncateCell = (value: string, sx: Record<string, unknown>) => {
      const text = value || '—';
      return (
        <TableCell sx={sx}>
          <Tooltip title={text === '—' ? '' : text} disableHoverListener={text === '—' || text.length < 12}>
            <Box component="span" sx={vatTxTruncateSx}>
              {text}
            </Box>
          </Tooltip>
        </TableCell>
      );
    };
    const sortHeader = (
      label: string,
      key: VatTxSortKey,
      sx: Record<string, unknown>,
      className?: string,
    ) => (
      <TableCell
        key={key}
        align="center"
        className={className}
        sx={sx}
        sortDirection={txSortKey === key ? txSortDir : false}
      >
        <TableSortLabel
          active={txSortKey === key}
          direction={txSortKey === key ? txSortDir : 'asc'}
          hideSortIcon={txSortKey !== key}
          onClick={() => handleTxSort(key)}
        >
          {vatTxHeaderLabel(label)}
        </TableSortLabel>
      </TableCell>
    );
    const colgroup = vatTxColgroup(
      vatRateColumns.length,
      vatTxFitWidths,
      isAnnual ? VAT_TX_COMPACT.actionsAnnual : VAT_TX_COMPACT.actionsVat
    );
    return (
      <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        <Table size="small" sx={VAT_TX_TABLE_SX}>
          <colgroup>
            {colgroup.map((col) => (
              <col
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
              />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                className="vat-tx-actions"
                sx={vatTxColSx.select}
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  size="small"
                  checked={allSectionSelected}
                  indeterminate={!allSectionSelected && someSectionSelected}
                  onChange={(_, checked) => toggleSectionSelectAll(displayRows, checked)}
                  inputProps={{ 'aria-label': 'Select all transactions in section' }}
                />
              </TableCell>
              {sortHeader('TX\nID', 'id', vatTxColSx.id, 'vat-tx-id')}
              {sortHeader('Date', 'date', vatTxColSx.date, 'vat-tx-date')}
              {sortHeader('Who', 'who', vatTxColSx.who, 'vat-tx-who')}
              {sortHeader('Description', 'description', vatTxColSx.description)}
              {sortHeader('Supplier', 'supplier', vatTxColSx.supplier)}
              {sortHeader('Category', 'category', vatTxColSx.category)}
              {sortHeader('Orig\nCcy', 'ccy', vatTxColSx.ccy)}
              {sortHeader('VAT\nrate', 'vat_rate', vatTxColSx.rate)}
              {sortHeader('Gross\nTotal', 'total', vatTxColSx.amountHeader)}
              {sortHeader('Net', 'net', vatTxColSx.netHeader)}
              {vatRateColumns.map((band) =>
                sortHeader(`VAT\n@ ${band}`, `band:${band}`, vatTxColSx.bandHeader(band))
              )}
              {sortHeader('Total\nVAT', 'vat', vatTxColSx.vatHeader)}
              <TableCell align="center" className="vat-tx-actions" sx={vatTxColSx.actions}>{vatTxHeaderLabel('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center">
                  <Typography color="text.secondary">No transactions in this section.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row) => {
                const hasReceipt = Boolean(documentUrlFor(row, 'receipt'));
                const hasSalesInvoice = Boolean(documentUrlFor(row, 'salesInvoice'));
                const hasPurchase = Boolean(documentUrlFor(row, 'purchase'));
                const claimed = isVatClaimed(row);
                const detailsOpen = detailPanelOpen && selectedRow?.id === row.id;
                const amounts = rowAmountBreakdown(row);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={detailsOpen || selectedTxIds.has(row.id)}
                    onClick={() => openRowDetail(row)}
                    sx={{
                      cursor: 'pointer',
                      ...(detailsOpen
                        ? {
                            bgcolor: '#E3F2FD',
                            '&.Mui-selected': { bgcolor: '#E3F2FD' },
                            '&.Mui-selected:hover': { bgcolor: '#BBDEFB' },
                            '&:hover': { bgcolor: '#BBDEFB' },
                          }
                        : {}),
                    }}
                  >
                    <TableCell
                      align="center"
                      className="vat-tx-actions"
                      sx={vatTxColSx.select}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedTxIds.has(row.id)}
                        onChange={(_, checked) => toggleTxSelected(row.id, checked)}
                        inputProps={{ 'aria-label': `Select TX ${row.id}` }}
                      />
                    </TableCell>
                    <TableCell className="vat-tx-id" sx={vatTxColSx.id}>{row.id}</TableCell>
                    <TableCell className="vat-tx-date" sx={vatTxColSx.date}>{formatRowDate(row)}</TableCell>
                    <TableCell align="center" className="vat-tx-who" sx={vatTxColSx.who}>
                      {(() => {
                        const who = vatWhoInitialsForRow(row);
                        return (
                          <Chip
                            size="small"
                            label={who}
                            variant="filled"
                            title={vatWhoTooltipForRow(row)}
                            sx={{
                              ...whoChipSx(who),
                              height: 22,
                              maxWidth: 'none',
                              '& .MuiChip-label': {
                                overflow: 'visible',
                                textOverflow: 'clip',
                                px: 0.75,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              },
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    {truncateCell(String(row.description || '').trim() || '—', vatTxColSx.description)}
                    {truncateCell(formatSupplier(row), vatTxColSx.supplier)}
                    {truncateCell(formatCategory(row), vatTxColSx.category)}
                    <TableCell align="center" sx={vatTxColSx.ccy}>
                      {originalCurrencyForRow(row) || '-'}
                    </TableCell>
                    <TableCell sx={vatTxColSx.rate}>{row.vat_rate || '-'}</TableCell>
                    <TableCell align="right" sx={vatTxColSx.total}>
                      {formatTableAmount(amounts.total, row.currency)}
                    </TableCell>
                    <TableCell align="right" sx={vatTxColSx.net}>
                      {formatTableAmount(amounts.net, row.currency)}
                    </TableCell>
                    {vatRateColumns.map((band) => {
                      const bandVat = rowVatAmountForBand(row, band);
                      return (
                        <TableCell key={band} align="right" sx={vatTxColSx.band(band)}>
                          {bandVat == null ? '-' : formatTableAmount(bandVat, row.currency)}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={vatTxColSx.vat}>
                      {formatTableAmount(amounts.vat, row.currency)}
                    </TableCell>
                    <TableCell align="right" className="vat-tx-actions" sx={vatTxColSx.actions} onClick={(event) => event.stopPropagation()}>
                      <Stack
                        direction="row"
                        spacing={0}
                        justifyContent="flex-end"
                        useFlexGap
                        sx={{
                          '& .MuiIconButton-root': VAT_TX_ACTION_BTN_SX,
                          '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                        }}
                      >
                        <Tooltip title={detailsOpen ? 'Details open in right panel' : 'Open details in right panel'}>
                          <IconButton
                            size="small"
                            aria-label={`Open details for TX ${row.id}`}
                            color={detailsOpen ? 'primary' : 'default'}
                            onClick={() => openRowDetail(row)}
                          >
                            <ViewSidebarIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasReceipt ? 'Receipt linked — click to upload / replace' : 'No receipt linked — click to upload'}>
                          <IconButton
                            size="small"
                            aria-label={hasReceipt ? `Receipt linked for TX ${row.id}; upload or replace` : `Upload receipt for TX ${row.id}`}
                            onClick={() => openUploadDialog('receipt', row)}
                            sx={hasReceipt
                              ? { color: 'primary.main' }
                              : { color: 'action.disabled' }}
                          >
                            <ReceiptLongIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasSalesInvoice ? 'Sales invoice linked — click to upload / replace' : 'No sales invoice linked — click to upload'}>
                          <IconButton
                            size="small"
                            aria-label={hasSalesInvoice ? `Sales invoice linked for TX ${row.id}; upload or replace` : `Upload sales invoice for TX ${row.id}`}
                            onClick={() => openUploadDialog('sales_invoice', row)}
                            sx={hasSalesInvoice
                              ? { color: 'primary.main' }
                              : { color: 'action.disabled' }}
                          >
                            <RequestQuoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasPurchase ? 'Purchase document linked — click to upload / replace' : 'No purchase document linked — click to upload'}>
                          <IconButton
                            size="small"
                            aria-label={hasPurchase ? `Purchase document linked for TX ${row.id}; upload or replace` : `Upload purchase document for TX ${row.id}`}
                            onClick={() => openUploadDialog('purchase_invoice', row)}
                            sx={hasPurchase
                              ? { color: 'primary.main' }
                              : { color: 'action.disabled' }}
                          >
                            <DescriptionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {cashflowBucket(row) === 'out' ? (
                          <Tooltip title={supplierInvoiceFetchTooltip(row)}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Fetch supplier invoice for TX ${row.id}`}
                                disabled={invoiceFetchBusy || !supplierInvoiceFetchReady(row)}
                                onClick={() => void enqueueInvoiceFetch([row.id])}
                                sx={
                                  supplierInvoiceSessionHealth(row)?.status === 'expiring_soon'
                                    ? { color: 'warning.main' }
                                    : supplierInvoiceFetchReady(row)
                                      ? { color: 'primary.main' }
                                      : { color: 'action.disabled' }
                                }
                              >
                                <CloudDownloadIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {!isAnnual ? (
                        <Tooltip
                          title={
                            claimed
                              ? 'VAT claimed — click to mark unclaimed'
                              : 'VAT not claimed — click to mark claimed'
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={
                              claimed
                                ? `VAT claimed for TX ${row.id}; mark unclaimed`
                                : `VAT not claimed for TX ${row.id}; mark claimed`
                            }
                            onClick={() => void toggleVatClaimed(row)}
                            sx={claimed ? { color: 'primary.main' } : { color: 'action.disabled' }}
                          >
                            <TaskAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {sectionTotals.length > 0 ? (
            <TableFooter>
              <TableRow
                sx={{
                  bgcolor: 'action.selected',
                  '& td': {
                    fontWeight: 700,
                    borderTop: 2,
                    borderColor: 'divider',
                    textAlign: 'center',
                  },
                }}
              >
                {/* Only label columns that have totals below; leave the rest blank. */}
                <TableCell colSpan={7} sx={vatTxColSx.description} />
                <TableCell align="center" sx={vatTxColSx.ccy}>{vatTxHeaderLabel('Orig\nCcy')}</TableCell>
                <TableCell align="center" sx={vatTxColSx.rate}>{vatTxHeaderLabel('VAT\nrate')}</TableCell>
                <TableCell align="center" sx={vatTxColSx.amountHeader}>
                  {vatTxHeaderLabel('Gross\nTotal')}
                </TableCell>
                <TableCell align="center" sx={vatTxColSx.netHeader}>
                  {vatTxHeaderLabel('Net')}
                </TableCell>
                {vatRateColumns.map((band) => (
                  <TableCell key={`footer-h-${band}`} align="center" sx={vatTxColSx.bandHeader(band)}>
                    {vatTxBandHeaderLabel(band)}
                  </TableCell>
                ))}
                <TableCell align="center" sx={vatTxColSx.vatHeader}>
                  {vatTxHeaderLabel('Total\nVAT')}
                </TableCell>
                <TableCell className="vat-tx-actions" sx={vatTxColSx.actions} />
              </TableRow>
              {sectionTotals.map((totals) => (
                <TableRow
                  key={`total-${totals.currency}`}
                  sx={{
                    bgcolor: 'action.hover',
                    '& td': { fontWeight: 700, borderTop: 1, borderColor: 'divider' },
                  }}
                >
                  <TableCell colSpan={7}>
                    Total{sectionTotals.length > 1 ? ` (${totals.currency})` : ''}
                    {' '}
                    · {totals.count} TX
                  </TableCell>
                  <TableCell sx={vatTxColSx.ccy}>{totals.currency}</TableCell>
                  <TableCell sx={vatTxColSx.rate} />
                  <TableCell align="right" sx={vatTxColSx.total}>
                    {formatTableAmount(totals.total, totals.currency)}
                  </TableCell>
                  <TableCell align="right" sx={vatTxColSx.net}>
                    {formatTableAmount(totals.net, totals.currency)}
                  </TableCell>
                  {vatRateColumns.map((band) => (
                    <TableCell key={band} align="right" sx={vatTxColSx.band(band)}>
                      {formatTableAmount(totals.bandVat[band] || 0, totals.currency)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={vatTxColSx.vat}>
                    {formatTableAmount(totals.vat, totals.currency)}
                  </TableCell>
                  <TableCell className="vat-tx-actions" sx={vatTxColSx.actions} />
                </TableRow>
              ))}
            </TableFooter>
          ) : null}
        </Table>
      </TableContainer>
    );
  };

  const docsForType = (type: 'receipt' | 'sales_invoice' | 'purchase_invoice') => {
    return allDocuments
      .filter((d) => d.document_type === type)
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  };

  const handleDocTypeSelection = (
    type: 'receipt' | 'sales_invoice' | 'purchase_invoice',
    event: SelectChangeEvent
  ) => {
    const value = event.target.value;
    setSelectedDocByType((prev) => ({ ...prev, [type]: value }));
  };

  const refreshResolvedForSelected = async () => {
    if (!selectedRow?.id) return;
    const res = await api.get('/api/transaction-documents/resolved', {
      params: { transaction_id: selectedRow.id },
    });
    const list = Array.isArray(res.data?.rows) ? res.data.rows : [];
    const row = list[0];
    if (!row) return;
    setResolvedDocsByTxId((prev) => ({
      ...prev,
      [selectedRow.id]: {
        receipt: row?.documents?.receipt ?? null,
        sales_invoice: row?.documents?.sales_invoice ?? null,
        purchase_invoice: row?.documents?.purchase_invoice ?? null,
      },
    }));
  };

  const linkDocument = async (type: 'receipt' | 'sales_invoice' | 'purchase_invoice') => {
    if (!selectedRow?.id) return;
    const uid = selectedDocByType[type];
    if (!uid) return;
    try {
      setLinking(true);
      setError('');
      await api.post('/api/transaction-documents/link', {
        transaction_id: selectedRow.id,
        document_type: type,
        document_uid: uid,
      });
      await refreshResolvedForSelected();
      setSelectedDocByType((prev) => ({ ...prev, [type]: '' }));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to link document');
    } finally {
      setLinking(false);
    }
  };

  const unlinkDocument = async (linkId: number) => {
    try {
      setUnlinkingLinkId(linkId);
      setError('');
      await api.delete(`/api/transaction-documents/${linkId}`);
      await refreshResolvedForSelected();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to unlink document');
    } finally {
      setUnlinkingLinkId(null);
    }
  };

  const openUploadDialog = (
    type: 'receipt' | 'sales_invoice' | 'purchase_invoice',
    row: VatRow | null = null
  ) => {
    const target = row ?? selectedRow;
    if (row) setSelectedRow(row);
    const d = target ? rowDate(target) : null;
    const isoDate = d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : '';
    setUploadDocType(type);
    setUploadFile(null);
    setUploadDescription(String(target?.description || '').trim());
    setUploadInvoiceNumber(target?.id ? `TX-${target.id}` : '');
    setUploadInvoiceDate(isoDate);
    setUploadSupplierName(String(target?.supplier || '').trim());
    setUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
    setUploadFile(null);
  };

  const uploadAndLinkDocument = async () => {
    if (!selectedRow?.id) return;
    if (!uploadFile) {
      setError('Please choose a file to upload');
      return;
    }
    if (uploadDocType !== 'receipt') {
      if (!uploadInvoiceNumber.trim() || !uploadInvoiceDate.trim()) {
        setError('Invoice number and invoice date are required');
        return;
      }
    }
    if (uploadDocType === 'purchase_invoice' && !uploadSupplierName.trim()) {
      setError('Supplier name is required for purchase invoices');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('document_type', uploadDocType);
      formData.append('file', uploadFile);
      if (uploadDescription.trim()) formData.append('description', uploadDescription.trim());
      if (uploadDocType !== 'receipt') {
        formData.append('invoice_number', uploadInvoiceNumber.trim());
        formData.append('invoice_date', uploadInvoiceDate.trim());
      }
      if (uploadDocType === 'purchase_invoice') {
        formData.append('supplier_name', uploadSupplierName.trim());
      }
      if (uploadDocType === 'receipt' && uploadInvoiceDate.trim()) {
        formData.append('receipt_date', uploadInvoiceDate.trim());
      }
      // Let the browser set multipart boundary; allow longer than the default 90s API timeout.
      const uploadRes = await api.post('/api/documents/upload', formData, {
        timeout: 180000,
      });
      const uid = String(uploadRes.data?.uid || '').trim();
      if (!uid) {
        throw new Error('Upload succeeded but document uid was missing');
      }
      await api.post('/api/transaction-documents/link', {
        transaction_id: selectedRow.id,
        document_type: uploadDocType,
        document_uid: uid,
      });
      // Unblock the dialog as soon as the TX link is live; refresh catalog in the background.
      await refreshResolvedForSelected();
      setUploadDialogOpen(false);
      setUploadFile(null);
      void fetchAllDocuments(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100%', overflowX: 'clip' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" sx={{ mb: 0 }}>
          {isAnnual ? 'Annual Returns' : 'VAT'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={() => {
            void exportActiveTabToXlsx();
          }}
          disabled={loading || exporting || sortedRows.length === 0}
          sx={{ flexShrink: 0 }}
        >
          {exporting ? 'Preparing…' : 'Export XLSX'}
        </Button>
      </Stack>
      <Card sx={{ maxWidth: '100%', overflowX: 'clip', overflowY: 'visible' }}>
        <CardContent sx={{ px: { xs: 1, sm: 2 }, '&:last-child': { pb: 2 } }}>
          <Box
            data-testid={TAX_RETURN_FILTER_BAR_TEST_ID}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              bgcolor: 'background.paper',
              mx: { xs: -1, sm: -2 },
              px: { xs: 1, sm: 2 },
              pt: 0.5,
              pb: 1,
              mb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              boxShadow: '0 1px 0 0 rgba(0,0,0,0.08)',
            }}
          >
          <Tabs
            value={regionFilter}
            onChange={(_, value: RegionFilter) => {
              setRegionFilter(value);
              if (isAnnual) setOriginalCurrencyFilter('all');
            }}
            sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="ire"
              label={`IE (${regionTabCounts.ire})`}
              id={`${idPrefix}-region-tab-ie`}
              aria-controls={`${idPrefix}-region-panel-ie`}
            />
            <Tab
              value="uk"
              label={`UK (${regionTabCounts.uk})`}
              id={`${idPrefix}-region-tab-uk`}
              aria-controls={`${idPrefix}-region-panel-uk`}
            />
          </Tabs>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 0 }}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id={`${idPrefix}-year-filter`}>Year</InputLabel>
              <Select
                labelId={`${idPrefix}-year-filter`}
                label="Year"
                value={String(selectedYear)}
                onChange={(event: SelectChangeEvent) => {
                  setSelectedYear(Number(event.target.value));
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={String(year)}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isAnnual ? (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id={`${idPrefix}-side-filter`}>Side</InputLabel>
              <Select
                labelId={`${idPrefix}-side-filter`}
                label="Side"
                value={cashflowFilter}
                onChange={(event: SelectChangeEvent) => {
                  setCashflowFilter(event.target.value as CashflowSideFilter);
                  setCashSideFilter('all');
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="in">Income</MenuItem>
                <MenuItem value="out">Expenses</MenuItem>
              </Select>
            </FormControl>
            ) : (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="vat-bimonth-filter">Period</InputLabel>
              <Select
                labelId="vat-bimonth-filter"
                label="Period"
                value={String(selectedBimonth)}
                onChange={(event: SelectChangeEvent) => {
                  setSelectedBimonth(Number(event.target.value) as BimonthPeriod);
                }}
              >
                {BIMONTH_PERIODS.map((period) => (
                  <MenuItem key={period.value} value={String(period.value)}>{period.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="vat-revolut-status-filter">Revolut status</InputLabel>
              <Select
                labelId="vat-revolut-status-filter"
                label="Revolut status"
                value={revolutStatusFilter}
                onChange={(event: SelectChangeEvent) => {
                  setRevolutStatusFilter(event.target.value as RevolutStatusFilter);
                }}
              >
                <MenuItem value="completed">Completed only</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="vat-original-currency-filter">Original currency</InputLabel>
              <Select
                labelId="vat-original-currency-filter"
                label="Original currency"
                value={originalCurrencyFilter}
                onChange={(event: SelectChangeEvent) => {
                  setOriginalCurrencyFilter(event.target.value as OriginalCurrencyFilter);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {originalCurrencyOptions.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code === 'EUR' ? 'EUR (Euro)' : code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="vat-committed-filter">Committed</InputLabel>
              <Select
                labelId="vat-committed-filter"
                label="Committed"
                value={committedFilter}
                onChange={(event: SelectChangeEvent) => {
                  setCommittedFilter(event.target.value as CommittedFilter);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="only">Committed only</MenuItem>
                <MenuItem value="exclude">Uncommitted only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="vat-tags-filter">Tags</InputLabel>
              <Select
                labelId="vat-tags-filter"
                label="Tags"
                value={tagsFilter}
                onChange={(event: SelectChangeEvent) => {
                  setTagsFilter(event.target.value as TagsFilter);
                }}
                renderValue={(value) => {
                  if (value === 'all') return 'All';
                  if (value === 'complete') return ALL_TAGS_STATUS;
                  return String(value);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="complete">{ALL_TAGS_STATUS}</MenuItem>
                {isAnnual ? null : <MenuItem value={VAT_CONFLICT_STATUS}>{VAT_CONFLICT_STATUS}</MenuItem>}
                <ListSubheader>Missing (1)</ListSubheader>
                {(isAnnual ? TAG_MISSING_STATUS_ANNUAL : TAG_MISSING_STATUS_PERMUTATIONS)
                  .filter((s) => countMissingDimsInStatus(s) === 1)
                  .map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
                <ListSubheader>Missing (2)</ListSubheader>
                {(isAnnual ? TAG_MISSING_STATUS_ANNUAL : TAG_MISSING_STATUS_PERMUTATIONS)
                  .filter((s) => countMissingDimsInStatus(s) === 2)
                  .map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
                <ListSubheader>Missing (3)</ListSubheader>
                {(isAnnual ? TAG_MISSING_STATUS_ANNUAL : TAG_MISSING_STATUS_PERMUTATIONS)
                  .filter((s) => countMissingDimsInStatus(s) === 3)
                  .map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
                {isAnnual ? null : (
                  <>
                <ListSubheader>Missing (4)</ListSubheader>
                {TAG_MISSING_STATUS_PERMUTATIONS.filter((s) => countMissingDimsInStatus(s) === 4).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
                  </>
                )}
                {tagsFilter !== 'all'
                  && tagsFilter !== 'complete'
                  && tagsFilter !== VAT_CONFLICT_STATUS
                  && !TAG_MISSING_STATUS_PERMUTATIONS.includes(tagsFilter) ? (
                  <MenuItem value={tagsFilter}>{tagsFilter}</MenuItem>
                ) : null}
              </Select>
            </FormControl>
            {isAnnual ? null : (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideZeroVatRows}
                  onChange={(_, checked) => setHideZeroVatRows(checked)}
                />
              }
              label="Hide zero VAT"
              sx={{ ml: 0.5, mr: 0, userSelect: 'none' }}
            />
            )}
            {isAnnual ? null : (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="vat-claimed-filter">VAT claimed</InputLabel>
              <Select
                labelId="vat-claimed-filter"
                label="VAT claimed"
                value={vatClaimedFilter}
                onChange={(event: SelectChangeEvent) => {
                  setVatClaimedFilter(event.target.value as VatClaimedFilter);
                }}
              >
                <MenuItem value="unclaimed">Unclaimed</MenuItem>
                <MenuItem value="claimed">Claimed</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={anyPageAccordionOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              onClick={() => setAllPageAccordions(!anyPageAccordionOpen)}
              disabled={loading || sortedRows.length === 0}
              aria-label={anyPageAccordionOpen ? 'Close all sections' : 'Open all sections'}
              sx={{ ml: { sm: 'auto' } }}
            >
              {anyPageAccordionOpen ? 'Close all sections' : 'Open all sections'}
            </Button>
          </Stack>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {invoiceFetchStatus ? (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInvoiceFetchStatus('')}>
              {invoiceFetchStatus}
            </Alert>
          ) : null}
          {selectedTxIds.size > 0 && !isAnnual ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    color="inherit"
                    size="small"
                    disabled={vatClaimBusy || invoiceFetchBusy}
                    onClick={() => void bulkSetVatClaimed(true)}
                  >
                    Mark claimed
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={vatClaimBusy || invoiceFetchBusy}
                    onClick={() => void bulkSetVatClaimed(false)}
                  >
                    Mark unclaimed
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={vatClaimBusy || invoiceFetchBusy}
                    onClick={() => void bulkFetchSupplierInvoices()}
                  >
                    Fetch invoices
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={vatClaimBusy || invoiceFetchBusy}
                    onClick={() => setSelectedTxIds(new Set())}
                  >
                    Clear
                  </Button>
                </Stack>
              }
            >
              {selectedTxIds.size} transaction{selectedTxIds.size === 1 ? '' : 's'} selected
            </Alert>
          ) : null}
          {selectedPortalSessionWarnings.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Portal session warning
              {selectedPortalSessionWarnings.length === 1 ? '' : 's'}:{' '}
              {selectedPortalSessionWarnings.join(' · ')}
            </Alert>
          ) : null}
          {rowsTruncated ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Showing a capped set of transactions for this {isAnnual ? 'year' : 'period'}.
              Narrow filters or contact support if you need the full set.
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : sortedRows.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No transactions found for this filter.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {(() => {
                const summaryExpanded = isPageAccordionOpen(VAT_SUMMARY_SECTION_KEY, true);
                return (
              <Accordion
                expanded={summaryExpanded}
                onChange={(_, expanded) =>
                  startTransition(() => setPageAccordionExpanded(VAT_SUMMARY_SECTION_KEY, expanded))
                }
                disableGutters
                slotProps={{ transition: { unmountOnExit: true } }}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="vat-summary-content"
                  id="vat-summary-header"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ pr: 1, width: '100%' }}
                  >
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      Summary · {selectedPeriodRangeLabel}
                    </Typography>
                    {isAnnual ? (
                      renderAnnualSummaryHeadingTotals()
                    ) : (
                      <>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {sortedRows.length} TX
                    </Typography>
                    {summaryInMinusOut.map((totals) => (
                      <Typography
                        key={`summary-in-minus-out-${totals.currency}`}
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2 }}
                      >
                            · In−Out Gross {formatAmountWhole(totals.gross, totals.currency)}
                            {' · '}
                            Net {formatAmountWhole(totals.net, totals.currency)}
                            {' · '}
                            VAT {formatAmountWhole(totals.vat, totals.currency)}
                      </Typography>
                    ))}
                      </>
                    )}
                  </Stack>
                </AccordionSummary>
                {summaryExpanded ? (
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                        Chart
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {isAnnual
                          ? 'P&L vs Cash (In above zero · Out below zero · Net). Category and supplier charts are in each section below. All FX converted to EUR — click a column to filter'
                          : 'Net + VAT (= Gross) · VAT In − Out'}
                      </Typography>
                      {isAnnual ? (
                        activeCombinedCharts.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No chart data for {activeRegionLabel}.
                          </Typography>
                        ) : (
                          <Stack spacing={2}>
                            {activeCombinedCharts.map((chart) =>
                              renderAnnualCombinedChart(chart, activeRegionLabel, regionFilter)
                            )}
                          </Stack>
                        )
                      ) : summaryChartByCurrency.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No chart data for this filter.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {summaryChartByCurrency.map((chart) => {
                            const moneyTip = (value: number | string, name: string) => [
                              typeof value === 'number' ? formatAmountWhole(value, chart.currency) : value,
                              name,
                            ];
                            return (
                              <Box key={`summary-chart-${chart.currency}`}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                  Cashflow In vs Out · VAT net ({chart.currency})
                                </Typography>
                                <Box sx={{ width: '100%', height: 320 }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                      data={chart.sideRows}
                                      margin={{ top: 36, right: 16, left: 8, bottom: 8 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="side" />
                                      <YAxis width={56} tickFormatter={formatChartTickWhole} />
                                      <RechartsTooltip
                                        formatter={moneyTip}
                                        labelFormatter={(label, payload) => {
                                          const row = payload?.[0]?.payload as
                                            | { Gross?: number; side?: string }
                                            | undefined;
                                          const gross = Number(row?.Gross);
                                          if (row?.side && Number.isFinite(gross) && gross !== 0) {
                                            return `${label} · Gross ${formatAmountWhole(gross, chart.currency)}`;
                                          }
                                          return String(label);
                                        }}
                                      />
                                      <Legend />
                                      <Bar dataKey="Net" stackId="totals" fill="#16A34A" isAnimationActive={false} />
                                      <Bar dataKey="VAT" stackId="totals" fill="#F59E0B" isAnimationActive={false}>
                                        <LabelList
                                          dataKey="labelTotal"
                                          position="top"
                                          offset={8}
                                          fill="#334155"
                                          fontSize={11}
                                          fontWeight={600}
                                          formatter={(label) => {
                                            const n = Number(label);
                                            if (!Number.isFinite(n)) return '';
                                            return formatAmountWhole(n, chart.currency);
                                          }}
                                        />
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                    {!isAnnual && cashflowVatSummaries.map((section) => (
                      <Box key={`summary-${section.key}`}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {section.title} · {selectedPeriodRangeLabel}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {section.rows.length} TX
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => scrollToVatDetail([section.key])}
                          >
                            View Details
                          </Button>
                          {section.summaries.map((summary) => (
                            <Typography
                              key={`${section.key}-title-${summary.region}-${summary.currency}`}
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}
                            >
                              · Gross {formatAmountWhole(summary.gross, summary.currency)}
                              {' · '}
                              Net {formatAmountWhole(summary.net, summary.currency)}
                              {' · '}
                              VAT {formatAmountWhole(summary.vat, summary.currency)}
                            </Typography>
                          ))}
                        </Stack>
                        {section.summaries.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No summary totals in this section.
                          </Typography>
                        ) : (
                          <Stack spacing={1.5}>
                            {section.summaries.map((summary) => {
                              const chartHeight = Math.max(
                                260,
                                Math.min(420, 140 + summary.bands.length * 28)
                              );
                              const moneyTip = (value: number | string, name: string) => [
                                typeof value === 'number' ? formatAmountWhole(value, summary.currency) : value,
                                name,
                              ];
                              return (
                                <Box
                                  key={`${section.key}-${summary.region}-${summary.currency}`}
                                  sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                                >
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {section.title} · {summary.region} ({summary.currency})
                                  </Typography>
                                  <Box sx={{ width: '100%', height: chartHeight }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart
                                        data={summary.bands.map((band) => ({
                                          band: band.band,
                                          Gross: Number(band.gross.toFixed(2)),
                                          Net: Number(band.net.toFixed(2)),
                                          VAT: Number(band.vat.toFixed(2)),
                                          count: band.count,
                                          labelTotal: Number(band.gross.toFixed(2)),
                                        }))}
                                        margin={{ top: 36, right: 12, left: 0, bottom: 8 }}
                                      >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="band" tick={{ fontSize: 12 }} />
                                        <YAxis width={52} tickFormatter={formatChartTickWhole} />
                                        <RechartsTooltip
                                          formatter={moneyTip}
                                          labelFormatter={(label, payload) => {
                                            const row = payload?.[0]?.payload as
                                              | { count?: number; Gross?: number }
                                              | undefined;
                                            const count = Number(row?.count);
                                            const parts = [String(label)];
                                            if (Number.isFinite(count) && count > 0) {
                                              parts.push(`${count} TX`);
                                            }
                                            return parts.join(' · ');
                                          }}
                                        />
                                        <Legend />
                                        <Bar dataKey="Net" stackId="band" fill="#16A34A" isAnimationActive={false} />
                                        <Bar dataKey="VAT" stackId="band" fill="#F59E0B" isAnimationActive={false}>
                                          <LabelList
                                            dataKey="labelTotal"
                                            position="top"
                                            offset={8}
                                            fill="#334155"
                                            fontSize={11}
                                            fontWeight={600}
                                            formatter={(label) => {
                                              const n = Number(label);
                                              if (!Number.isFinite(n)) return '';
                                              return formatAmountWhole(n, summary.currency);
                                            }}
                                          />
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
                ) : null}
              </Accordion>
                );
              })()}
              {isAnnual
                ? (() => {
                    const familyExpanded = isPageAccordionOpen(
                      FAMILY_TRANSFERS_SECTION_KEY,
                      familyTransfersView.rows.length > 0
                    );
                    return (
                      <Accordion
                        key={FAMILY_TRANSFERS_SECTION_KEY}
                        id={vatDetailSectionId(FAMILY_TRANSFERS_SECTION_KEY)}
                        expanded={familyExpanded}
                        onChange={(_, expanded) =>
                          startTransition(() =>
                            setPageAccordionExpanded(FAMILY_TRANSFERS_SECTION_KEY, expanded)
                          )
                        }
                        disableGutters
                        slotProps={{ transition: { unmountOnExit: true } }}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="annual-family-transfers-content"
                          id="annual-family-transfers-header"
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ pr: 1, width: '100%' }}
                          >
                            <Typography
                              variant="subtitle1"
                              component="span"
                              sx={{ fontWeight: 600, lineHeight: 1.2 }}
                            >
                              Family transfers · {selectedPeriodRangeLabel}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {renderAnnualInOutHeadingSegment(
                                'in',
                                familyTransfersView.totals.inAmount,
                                familyTransfersView.totals.inCount
                              )}
                              {renderAnnualInOutHeadingSegment(
                                'out',
                                Math.abs(familyTransfersView.totals.outAmount),
                                familyTransfersView.totals.outCount
                              )}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        {familyExpanded ? (
                          <AccordionDetails sx={{ px: { xs: 1, sm: 1.5 }, pb: 1, pt: 1 }}>
                            <Stack spacing={1.5}>
                              <Typography variant="body2" color="text.secondary">
                                Cash in and out for all three families (six people), matched by
                                supplier. Existing categories are shown as-is — nothing is
                                reclassified. Joint couple suppliers (e.g. Omar &amp; Heidi) sit
                                under the family, not on both people. Click a chart column to open
                                that family.
                              </Typography>
                              <AnnualSignedInOutChart
                                title={`Family transfers (${annualDisplayCurrency})`}
                                rows={familyTransferChartRows}
                                currency={annualDisplayCurrency}
                                minHeight={220}
                                keepZeroColumns
                                onLabelClick={(label) => {
                                  const chartRow = familyTransferChartRows.find(
                                    (row) => row.label === label
                                  );
                                  if (!chartRow) return;
                                  const nestedKey = familyTransferAccordionKey(chartRow.familyKey);
                                  startTransition(() => {
                                    setPageAccordionExpanded(FAMILY_TRANSFERS_SECTION_KEY, true);
                                    setPageAccordionExpanded(nestedKey, true);
                                  });
                                  window.setTimeout(() => {
                                    const target = document.getElementById(
                                      vatDetailSectionId(nestedKey)
                                    );
                                    if (!target) return;
                                    scrollSectionBelowStickyBar({
                                      target,
                                      scrollParent: document.getElementById(LAYOUT_MAIN_SCROLL_ID),
                                      stickyBar: document.querySelector<HTMLElement>(
                                        `[data-testid="${TAX_RETURN_FILTER_BAR_TEST_ID}"]`
                                      ),
                                    });
                                  }, 0);
                                }}
                              />
                              {familyTransfersView.families.map((family) => {
                                const nestedKey = familyTransferAccordionKey(family.familyKey);
                                const nestedExpanded = isPageAccordionOpen(
                                  nestedKey,
                                  family.rows.length > 0
                                );
                                return (
                                  <Accordion
                                    key={family.familyKey}
                                    id={vatDetailSectionId(nestedKey)}
                                    expanded={nestedExpanded}
                                    onChange={(_, expanded) =>
                                      startTransition(() =>
                                        setPageAccordionExpanded(nestedKey, expanded)
                                      )
                                    }
                                    disableGutters
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    sx={{
                                      border: 1,
                                      borderColor: 'divider',
                                      '&:before': { display: 'none' },
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      aria-controls={`annual-family-${family.familyKey}-content`}
                                      id={`annual-family-${family.familyKey}-header`}
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{ pr: 1, width: '100%' }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          component="span"
                                          sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                        >
                                          {family.familyName}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ lineHeight: 1.2 }}
                                        >
                                          {renderAnnualInOutHeadingSegment(
                                            'in',
                                            family.totals.inAmount,
                                            family.totals.inCount
                                          )}
                                          {renderAnnualInOutHeadingSegment(
                                            'out',
                                            Math.abs(family.totals.outAmount),
                                            family.totals.outCount
                                          )}
                                        </Typography>
                                      </Stack>
                                    </AccordionSummary>
                                    {nestedExpanded ? (
                                      <AccordionDetails
                                        sx={{ px: { xs: 1, sm: 1.25 }, pb: 1.25, pt: 0.5 }}
                                      >
                                        <Stack spacing={1.25}>
                                        <Stack spacing={1}>
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, minWidth: '6.5rem' }}
                                            >
                                              Cash in/Out:
                                            </Typography>
                                            {family.people.map((person) => (
                                              <Chip
                                                key={person.personKey}
                                                size="small"
                                                variant="outlined"
                                                label={`${person.name}: In ${formatAmountWhole(
                                                  person.totals.inAmount,
                                                  annualDisplayCurrency
                                                )} · Out ${formatAmountWhole(
                                                  Math.abs(person.totals.outAmount),
                                                  annualDisplayCurrency
                                                )} (${person.totals.count} Tx)`}
                                              />
                                            ))}
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              color={family.jointRows.length ? 'primary' : 'default'}
                                              label={`Joint: In ${formatAmountWhole(
                                                family.jointTotals.inAmount,
                                                annualDisplayCurrency
                                              )} · Out ${formatAmountWhole(
                                                Math.abs(family.jointTotals.outAmount),
                                                annualDisplayCurrency
                                              )} (${family.jointTotals.count} Tx)`}
                                            />
                                          </Stack>
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, minWidth: '6.5rem' }}
                                            >
                                              Taxable Income:
                                            </Typography>
                                            {family.people.map((person) => (
                                              <Chip
                                                key={`tax-${person.personKey}`}
                                                size="small"
                                                variant="outlined"
                                                label={`${person.name}: ${formatAmountWhole(
                                                  person.taxableIncomeAmount,
                                                  annualDisplayCurrency
                                                )} (${person.taxableIncomeCount} Tx)`}
                                              />
                                            ))}
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              color={
                                                family.jointTaxableIncomeCount > 0
                                                  ? 'primary'
                                                  : 'default'
                                              }
                                              label={`Joint: ${formatAmountWhole(
                                                family.jointTaxableIncomeAmount,
                                                annualDisplayCurrency
                                              )} (${family.jointTaxableIncomeCount} Tx)`}
                                            />
                                          </Stack>
                                        </Stack>
                                          {family.categoryBreakdown.length > 0 ? (
                                            <Stack spacing={0.5}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                By category
                                              </Typography>
                                              <Stack
                                                direction="row"
                                                spacing={0.75}
                                                useFlexGap
                                                flexWrap="wrap"
                                              >
                                                {family.categoryBreakdown.map((cat) => (
                                                  <Chip
                                                    key={`${family.familyKey}-${cat.category}`}
                                                    size="small"
                                                    label={`${cat.category}: In ${formatAmountWhole(
                                                      cat.inAmount,
                                                      annualDisplayCurrency
                                                    )} · Out ${formatAmountWhole(
                                                      Math.abs(cat.outAmount),
                                                      annualDisplayCurrency
                                                    )} (${cat.count})`}
                                                  />
                                                ))}
                                              </Stack>
                                            </Stack>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">
                                              No family-supplier transactions in this filter.
                                            </Typography>
                                          )}
                                          {family.rows.length > 0
                                            ? renderVatTxTable(family.rows, false)
                                            : null}
                                        </Stack>
                                      </AccordionDetails>
                                    ) : null}
                                  </Accordion>
                                );
                              })}
                            </Stack>
                          </AccordionDetails>
                        ) : null}
                      </Accordion>
                    );
                  })()
                : null}
              {cashflowVatSummaries.map((section) => {
                const sectionExpanded = isPageAccordionOpen(section.key, section.rows.length > 0);
                const filteredRows = sectionFilteredRowsByKey.get(section.key) ?? section.rows;
                const displayRows = sectionDisplayRowsByKey.get(section.key) ?? filteredRows;
                const signed = annualSectionSignedChartsByKey.get(section.key);
                return (
                <Accordion
                  key={section.key}
                  id={vatDetailSectionId(section.key)}
                  expanded={sectionExpanded}
                  onChange={(_, expanded) =>
                    startTransition(() => setPageAccordionExpanded(section.key, expanded))
                  }
                  disableGutters
                  slotProps={{ transition: { unmountOnExit: true } }}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`vat-cashflow-${section.key}-content`}
                    id={`vat-cashflow-${section.key}-header`}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ pr: 1, width: '100%' }}
                    >
                      <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {section.title} · {selectedPeriodRangeLabel}
                      </Typography>
                      {isAnnual
                        ? renderAnnualSectionHeadingTotals(section.key)
                        : renderVatSectionHeadingTotals(section)}
                    </Stack>
                  </AccordionSummary>
                  {sectionExpanded ? (
                  <AccordionDetails sx={{ px: { xs: 1, sm: 1.5 }, pb: 1, pt: 1 }}>
                    <Stack spacing={1.5}>
                      {isAnnual ? (
                            <AnnualSectionSignedChartPanel
                              sectionTitle={section.title}
                              displayCurrency={annualDisplayCurrency}
                              categoryRows={signed?.categoryChartRows || []}
                              supplierRows={
                                sectionSupplierChartRowsByKey.get(section.key) ||
                                signed?.supplierChartRows ||
                                []
                              }
                              activeSupplierFilter={
                                chartSupplierFilter?.sectionKey === section.key
                                  ? chartSupplierFilter.supplier
                                  : null
                              }
                              activeCategoryFilter={
                                chartCategoryFilter?.sectionKey === section.key
                                  ? chartCategoryFilter.category
                                  : null
                              }
                              onSupplierClick={(label) =>
                                applyChartSupplierColumnFilter(section.key, label)
                              }
                              onCategoryClick={(label) =>
                                applyChartCategoryColumnFilter(section.key, label)
                              }
                            />
                      ) : (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                          Chart by supplier
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Net + VAT (= Gross) · one column per supplier · click a column to filter the table
                        </Typography>
                        {(() => {
                          const supplierCharts =
                            cashflowSupplierCharts.find((c) => c.key === section.key)?.charts || [];
                          if (supplierCharts.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No supplier chart data for this section.
                              </Typography>
                            );
                          }
                          return (
                            <Stack spacing={2}>
                              {supplierCharts.map((chart) => {
                                const moneyTip = (value: number | string, name: string) => [
                                  typeof value === 'number' ? formatAmountWhole(value, chart.currency) : value,
                                  name,
                                ];
                                const supplierLabels = chart.suppliers.map((row) => row.supplier);
                                const dense = isDenseColumnChart(chart.suppliers.length);
                                const axisHeight = columnChartAxisHeight(supplierLabels, dense);
                                const chartHeight = columnChartHeight(axisHeight, dense, 244);
                                return (
                                  <Box key={`${section.key}-supplier-chart-${chart.currency}`}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                      {section.title} by supplier ({chart.currency})
                                    </Typography>
                                    <Box sx={{ width: '100%', height: chartHeight, cursor: 'pointer' }}>
                                      <ResponsiveContainer width="100%" height="100%">
                                          <BarChart
                                            data={chart.suppliers}
                                            margin={columnChartMargin(dense)}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <CategoryColumnXAxis
                                              dataKey="supplier"
                                              labels={supplierLabels}
                                              dense={dense}
                                            />
                                            <YAxis width={52} tickFormatter={formatChartTickWhole} />
                                            <RechartsTooltip
                                              formatter={moneyTip}
                                              labelFormatter={(label, payload) => {
                                                const row = payload?.[0]?.payload as
                                                  | { Gross?: number; count?: number; supplier?: string }
                                                  | undefined;
                                                const gross = Number(row?.Gross);
                                                const count = Number(row?.count);
                                                const parts = [String(label)];
                                                if (Number.isFinite(count) && count > 0) {
                                                  parts.push(`${count} TX`);
                                                }
                                                if (Number.isFinite(gross) && gross !== 0) {
                                                  parts.push(`Gross ${formatAmountWhole(gross, chart.currency)}`);
                                                }
                                                return parts.join(' · ');
                                              }}
                                            />
                                            <Legend verticalAlign="top" height={24} />
                                            <Bar
                                              dataKey="Net"
                                              stackId="supplier"
                                              fill="#16A34A"
                                              isAnimationActive={false}
                                              cursor="pointer"
                                              onClick={(item, index) =>
                                                applyChartSupplierColumnFilter(
                                                  section.key,
                                                  chartColumnLabelFromBarClick(
                                                    chart.suppliers.map((row) => ({
                                                      label: row.supplier,
                                                    })),
                                                    item,
                                                    index
                                                  )
                                                )
                                              }
                                            >
                                              {chart.suppliers.map((row) => (
                                                <Cell
                                                  key={`net-${row.supplier}`}
                                                  fill="#16A34A"
                                                  fillOpacity={supplierColumnOpacity(section.key, row.supplier)}
                                                />
                                              ))}
                                            </Bar>
                                            <Bar
                                              dataKey="VAT"
                                              stackId="supplier"
                                              fill="#F59E0B"
                                              isAnimationActive={false}
                                              cursor="pointer"
                                              onClick={(item, index) =>
                                                applyChartSupplierColumnFilter(
                                                  section.key,
                                                  chartColumnLabelFromBarClick(
                                                    chart.suppliers.map((row) => ({
                                                      label: row.supplier,
                                                    })),
                                                    item,
                                                    index,
                                                    item?.payload?.supplier
                                                  )
                                                )
                                              }
                                            >
                                              {chart.suppliers.map((row) => (
                                                <Cell
                                                  key={`vat-${row.supplier}`}
                                                  fill="#F59E0B"
                                                  fillOpacity={supplierColumnOpacity(section.key, row.supplier)}
                                                />
                                              ))}
                                              <LabelList
                                                dataKey="labelTotal"
                                                content={(props) => (
                                                  <OutsideColumnValueLabel
                                                    value={props.value}
                                                    viewBox={props.viewBox as { x?: number; y?: number; width?: number; height?: number }}
                                                    dense={dense}
                                                    hideZero={false}
                                                    format={(n) => formatAmountWhole(n, chart.currency)}
                                                  />
                                                )}
                                              />
                                            </Bar>
                                          </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Stack>
                          );
                        })()}
                      </Box>
                      )}
                      {(chartCategoryFilter?.sectionKey === section.key ||
                        chartSupplierFilter?.sectionKey === section.key) ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {chartCategoryFilter?.sectionKey === section.key ? (
                            <Chip
                              size="small"
                              color="primary"
                              label={`Category: ${chartCategoryFilter.category}`}
                              onDelete={() => clearSectionChartCategoryFilter(section.key)}
                              deleteIcon={<CloseIcon />}
                            />
                          ) : null}
                          {chartSupplierFilter?.sectionKey === section.key ? (
                            <Chip
                              size="small"
                              color="primary"
                              label={`Supplier: ${chartSupplierFilter.supplier}`}
                              onDelete={() => setChartSupplierFilter(null)}
                              deleteIcon={<CloseIcon />}
                            />
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            {filteredRows.length}
                            {' of '}
                            {section.rows.length} TX
                          </Typography>
                        </Stack>
                      ) : null}
                      {renderVatTxTable(displayRows, true)}
                    </Stack>
                  </AccordionDetails>
                  ) : null}
                </Accordion>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={detailPanelOpen && Boolean(selectedRow)}
        onClose={closeRowDetail}
        hideBackdrop
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          sx: {
            zIndex: (theme) => theme.zIndex.snackbar + 15,
            pointerEvents: 'none',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DETAIL_PANEL_WIDTH_PX },
            maxWidth: '100vw',
            zIndex: 1,
            pointerEvents: 'auto',
            borderLeft: 1,
            borderColor: 'divider',
            boxShadow: 12,
          },
        }}
      >
        {selectedRow ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                pt: (theme) => `max(${theme.spacing(2)}, calc(${theme.spacing(1)} + env(safe-area-inset-top, 0px)))`,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                  <Typography variant="h6" component="div" sx={{ lineHeight: 1.25 }}>
                    TX {selectedRow.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                    {formatRowDate(selectedRow)} · {formatRegion(selectedRow)} · {formatAmount(selectedRow.amount, selectedRow.currency)}
                  </Typography>
                </Box>
                <Tooltip title="Close panel">
                  <IconButton aria-label="Close TX detail panel" edge="end" size="medium" onClick={closeRowDetail}>
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Click another row to load it here without closing this panel.
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
              <Stack spacing={1}>
                <Accordion
                  defaultExpanded
                  disableGutters
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="vat-panel-details-content"
                    id="vat-panel-details-header"
                  >
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                      Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Date:</strong> {formatRowDate(selectedRow)}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Committed:</strong> {isRowCommitted(selectedRow) ? 'Yes' : 'No'}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Description:</strong> {selectedRow.description || '-'}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Region:</strong> {formatRegion(selectedRow)}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Applied status:</strong> {selectedRow.status || '-'}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Total Amount:</strong> {formatAmount(selectedAmounts?.total, selectedRow.currency)}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Net Amount:</strong> {formatAmount(selectedAmounts?.net, selectedRow.currency)}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>VAT Amount:</strong> {formatAmount(selectedAmounts?.vat, selectedRow.currency)}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}><strong>Orig currency:</strong> {originalCurrencyForRow(selectedRow) || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Internal: private notes. External: included on accountant-facing XLSX export.
                    </Typography>
                    {commentsNotice ? (
                      <Alert severity="success" sx={{ mb: 1 }} onClose={() => setCommentsNotice('')}>
                        {commentsNotice}
                      </Alert>
                    ) : null}
                    <TextField
                      label="Comment (internal)"
                      value={commentInternalDraft}
                      onChange={(e) => setCommentInternalDraft(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={{ mb: 1.25 }}
                      disabled={commentsSaving}
                      placeholder="Private notes…"
                    />
                    <TextField
                      label="Comment (external)"
                      value={commentExternalDraft}
                      onChange={(e) => setCommentExternalDraft(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={{ mb: 1.25 }}
                      disabled={commentsSaving}
                      placeholder="Notes for accountant / XLSX export…"
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        void saveAppendedComments();
                      }}
                      disabled={commentsSaving}
                    >
                      {commentsSaving ? 'Saving…' : 'Save comments'}
                    </Button>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  disableGutters
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="vat-panel-tags-content"
                    id="vat-panel-tags-header"
                  >
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                      Tags
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                      Edit Cash flow, Supplier, Category, VAT, and Prop. Changes save when you pick a value.
                    </Typography>
                    {tagNotice ? (
                      <Alert severity="success" sx={{ mb: 1.25 }} onClose={() => setTagNotice('')}>
                        {tagNotice}
                      </Alert>
                    ) : null}
                    <Stack spacing={1.25} sx={{ opacity: tagBusy ? 0.7 : 1, pointerEvents: tagBusy ? 'none' : 'auto' }}>
                      <FormControl size="small" fullWidth disabled={tagBusy}>
                        <InputLabel id="vat-tag-cashflow">Cash flow</InputLabel>
                        <Select
                          labelId="vat-tag-cashflow"
                          label="Cash flow"
                          value={tagDraft.cashflowId}
                          onChange={(event: SelectChangeEvent) => {
                            void saveCashflowTag(event.target.value);
                          }}
                          MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {headingCategories.map((c) => (
                            <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={tagBusy}>
                        <InputLabel id="vat-tag-supplier">Supplier</InputLabel>
                        <Select
                          labelId="vat-tag-supplier"
                          label="Supplier"
                          value={tagDraft.supplier}
                          onChange={(event: SelectChangeEvent) => {
                            void saveSupplierTag(event.target.value);
                          }}
                          MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {tagDraft.supplier
                            && !supplierOptions.includes(tagDraft.supplier) ? (
                            <MenuItem value={tagDraft.supplier}>{tagDraft.supplier}</MenuItem>
                          ) : null}
                          {supplierOptions.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={tagBusy}>
                        <InputLabel id="vat-tag-category">Category</InputLabel>
                        <Select
                          labelId="vat-tag-category"
                          label="Category"
                          value={tagDraft.categoryId}
                          onChange={(event: SelectChangeEvent) => {
                            void saveCategoryTag(event.target.value);
                          }}
                          MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {granularCategories.map((c) => (
                            <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={tagBusy}>
                        <InputLabel id="vat-tag-vat">VAT rate</InputLabel>
                        <Select
                          labelId="vat-tag-vat"
                          label="VAT rate"
                          value={tagDraft.vatRate}
                          onChange={(event: SelectChangeEvent) => {
                            void saveVatRateTag(event.target.value);
                          }}
                          MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                        >
                          <MenuItem value="">
                            <em>—</em>
                          </MenuItem>
                          {tagDraft.vatRate
                            && !vatRateOptions.includes(tagDraft.vatRate) ? (
                            <MenuItem value={tagDraft.vatRate}>{tagDraft.vatRate}</MenuItem>
                          ) : null}
                          {vatRateOptions.map((rate) => (
                            <MenuItem key={rate} value={rate}>{rate}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={tagBusy}>
                        <InputLabel id="vat-tag-prop">Prop</InputLabel>
                        <Select
                          labelId="vat-tag-prop"
                          label="Prop"
                          multiple
                          displayEmpty
                          value={tagDraft.propIds.map(String) as unknown as string}
                          onChange={(event) => {
                            const raw = event.target.value;
                            const values = Array.isArray(raw)
                              ? raw.map(String)
                              : String(raw || '')
                                  .split(',')
                                  .map((s) => s.trim())
                                  .filter(Boolean);
                            void savePropTags(values);
                          }}
                          renderValue={(selected) => {
                            const selectedArr = Array.isArray(selected)
                              ? selected
                              : String(selected || '')
                                  .split(',')
                                  .filter(Boolean);
                            const ids = selectedArr.map((v) => Number(v));
                            const labels = ids
                              .map((id) => propOptions.find((o) => o.id === id)?.nickname || String(id))
                              .filter(Boolean);
                            return labels.length ? labels.join(', ') : '—';
                          }}
                          MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                        >
                          {propOptions.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>{p.nickname}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {tagBusy ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : null}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  disableGutters
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="vat-panel-documents-content"
                    id="vat-panel-documents-header"
                  >
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                      Documents
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Receipt:</strong> {docCell(selectedRow, 'receipt', 'Open document')}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Sales Invoice:</strong> {docCell(selectedRow, 'salesInvoice', 'Open document')}</Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}><strong>Purchase:</strong> {docCell(selectedRow, 'purchase', 'Open document')}</Typography>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Manage linked documents
                    </Typography>
                    <Stack spacing={1.25}>
                      {([
                        ['receipt', 'Receipt'],
                        ['sales_invoice', 'Sales Invoice'],
                        ['purchase_invoice', 'Purchase'],
                      ] as Array<['receipt' | 'sales_invoice' | 'purchase_invoice', string]>).map(([type, label]) => {
                        const linked = type === 'receipt'
                          ? selectedTxDocs?.receipt
                          : type === 'sales_invoice'
                            ? selectedTxDocs?.sales_invoice
                            : selectedTxDocs?.purchase_invoice;
                        const unlinkId = linked?.link_id;
                        return (
                          <Box key={type} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{label}:</strong>{' '}
                              {linked?.file_name || linked?.document_uid || 'Not linked'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {linked?.open_url ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    const rowDocKind: DocKind = type === 'receipt'
                                      ? 'receipt'
                                      : type === 'sales_invoice'
                                        ? 'salesInvoice'
                                        : 'purchase';
                                    openDoc(selectedRow, rowDocKind);
                                  }}
                                >
                                  Open
                                </Button>
                              ) : null}
                              {unlinkId != null ? (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  disabled={unlinkingLinkId === unlinkId}
                                  onClick={() => unlinkDocument(unlinkId)}
                                >
                                  {unlinkingLinkId === unlinkId ? 'Unlinking...' : 'Unlink'}
                                </Button>
                              ) : null}

                              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                                <InputLabel id={`vat-link-${type}`}>Link {label}</InputLabel>
                                <Select
                                  labelId={`vat-link-${type}`}
                                  label={`Link ${label}`}
                                  value={selectedDocByType[type]}
                                  onChange={(event) => handleDocTypeSelection(type, event)}
                                  MenuProps={DETAIL_DRAWER_SELECT_MENU_PROPS}
                                >
                                  <MenuItem value="">Select document...</MenuItem>
                                  {docsForType(type).map((doc) => (
                                    <MenuItem key={doc.uid} value={doc.uid}>
                                      {doc.file_name || doc.uid}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={linking || !selectedDocByType[type]}
                                onClick={() => linkDocument(type)}
                              >
                                {linking ? 'Linking...' : 'Link'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={uploading}
                                onClick={() => openUploadDialog(type)}
                              >
                                Upload
                              </Button>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: 2, pt: 1.5, flexShrink: 0 }}>
              <Button variant="outlined" onClick={closeRowDetail}>
                Close panel
              </Button>
            </Box>
          </Box>
        ) : null}
      </Drawer>

      <Dialog open={Boolean(docUrl)} onClose={() => setDocUrl(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{docTitle}</DialogTitle>
        <DialogContent sx={{ height: '75vh', p: 0 }}>
          {docUrl ? (
            <iframe title={docTitle} src={docUrl} style={{ border: 0, width: '100%', height: '100%' }} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={uploadDialogOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload{' '}
          {uploadDocType === 'receipt'
            ? 'Receipt'
            : uploadDocType === 'sales_invoice'
              ? 'Sales Invoice'
              : 'Purchase Invoice'}
          {selectedRow?.id ? ` for TX ${selectedRow.id}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              File is stored in the same documents library as Receipts &amp; Invoices, then linked to this transaction.
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Button variant="outlined" component="label" disabled={uploading}>
              {uploadFile ? uploadFile.name : 'Choose file'}
              <input
                type="file"
                hidden
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
            <TextField
              label="Description"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              fullWidth
              size="small"
              disabled={uploading}
            />
            {uploadDocType !== 'receipt' ? (
              <>
                <TextField
                  label="Invoice Number"
                  value={uploadInvoiceNumber}
                  onChange={(e) => setUploadInvoiceNumber(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={uploading}
                />
                <TextField
                  label="Invoice Date"
                  type="date"
                  value={uploadInvoiceDate}
                  onChange={(e) => setUploadInvoiceDate(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={uploading}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            ) : (
              <TextField
                label="Receipt Date"
                type="date"
                value={uploadInvoiceDate}
                onChange={(e) => setUploadInvoiceDate(e.target.value)}
                fullWidth
                size="small"
                disabled={uploading}
                InputLabelProps={{ shrink: true }}
              />
            )}
            {uploadDocType === 'purchase_invoice' ? (
              <TextField
                label="Supplier Name"
                value={uploadSupplierName}
                onChange={(e) => setUploadSupplierName(e.target.value)}
                fullWidth
                size="small"
                required
                disabled={uploading}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog} disabled={uploading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void uploadAndLinkDocument()}
            disabled={uploading || !uploadFile}
          >
            {uploading ? 'Uploading…' : 'Upload & link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VAT;
